import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { MoreVertical, ArrowUp, ArrowDown, Tag, Trash2 } from 'lucide-react'
import * as XLSX from 'xlsx'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { DateInput } from '@/components/forms/DateInput'
import { formatDateFull } from '@/lib/dateFormat'
import { normalizeUomLabel } from '@/lib/uom'
import { supplierService, type Supplier } from '@/services/suppliers'
import { materialService, type Material } from '@/services/materials'
import { createPurchaseOrder, getPurchaseOrder, patchPurchaseOrder, patchPoLine, createPoLine, deletePoLine, money, type CreatePoPayload, type CreatePoLinePayload, type PoLineDto, type PurchaseOrderDto, type PatchPoLinePayload } from '@/services/procurement'
import { PoSignerEditor, readDefaultPoSigners, type PoSigner } from './PoSignerEditor'
import { DEFAULT_PO_SIGNERS, shouldAutoSavePoSignersDefault } from './PoSignerEditor'
import { getPoSignersSettings, savePoSignersSettings } from '@/services/settings/poSignersApi'
import { PoPriceSnapshotModal } from './PoPriceSnapshotModal'
import { useAuth } from '@/features/auth/AuthContext'
import { apiFetch } from '@/services/apiFetch'
import { capturePoSpec } from './specColor'
import type { PriceSnapshot } from '@/services/procurement/types'
import { previewPrice, resolvePriceApi } from '@/services/procurement/apiClient'
import { formatSheetAmount, parseSheetAmount } from '@/lib/sheetAmount'
import { SheetAmountInput } from '@/components/forms/SheetAmountInput'
import { displayCell } from '@/lib/cellDisplay'
import { choicesFromRule, inputNamesOf, pruneLaterChoices, sheetLineSpecAttributes, sheetMatchingRules, sheetOptionsFor, sheetSaveBlocker, type SheetChoices } from './poSheetChain'
import type { ActiveRuleOption } from './PoRulePickerModal'
import { PoRulePickerModal } from './PoRulePickerModal'
import {
  supplierFormatService,
  type SupplierFormatDef,
  type FormatColumnDef,
} from '@/services/suppliers/supplierFormatService'
import { listQuotesApi, listRulesByQuoteApi } from '@/services/quotes/apiClient'
import type { PriceRule, SupplierQuote } from '@/services/quotes'
import { QuoteChoiceInput } from './QuoteChoiceInput'
import { Typeahead, stripDiacritics } from '@/components/forms/Typeahead'
import {
  buildQuoteRuleSuggestions,
  filterQuoteRuleSuggestion,
  quoteRuleNameKey,
  quoteRuleFieldIsUsed,
  quoteRuleFieldSuggestions,
  quoteRuleMatchesItemName,
  quoteRuleToPoItemPatch,
  quoteNameSelectionPatch,
  type QuoteRuleSuggestion,
} from './poQuoteMapping'
import {
  FreeSheetTable,
  type FreeColumn,
  type FreeRow,
} from '@/components/table/FreeSheetTable'
import {
  CORE_COLUMNS,
  freeLineDetailName,
  newId,
  parseNumberVi,
  normalizeFreeLine,
} from '@/components/table/freeSheetModel'
import {
  createFreePo,
  updateFreePoContent,
  type FreeLayoutColumn,
} from '@/services/procurement/freePoApi'
import { PoPrintView } from '@/features/proto/PoPrintView'
import { mapPoDraftToPrintModel } from './poPrintModel'
import { foamQuoteItemNames } from '@/features/boms/foamBomQuoteSheet'

function makeInitialFreeRows(): FreeRow[] {
  return [
    { id: `row_${newId()}`, name: '', uom: 'cái', qty: '', unit_price: '', line_total: 0, note: '' },
    { id: `row_${newId()}`, name: '', uom: 'tấm', qty: '', unit_price: '', line_total: 0, note: '' },
    { id: `row_${newId()}`, name: '', uom: 'bộ', qty: '', unit_price: '', line_total: 0, note: '' },
  ]
}

export type PoQuoteType = string

export type PoPriceCandidate = {
  ruleId: string
  unitPrice: number
  ruleItemName?: string
  quoteTitle?: string
  quoteId?: string
  uom?: string
  spec?: string | null
  density?: string | null
}

export interface PoNameSuggestion {
  id: string
  type: 'quote' | 'material'
  name: string
  primaryText: string
  secondaryText: string
  searchText: string
  rule?: PriceRule
  quote?: SupplierQuote | null
  material?: Material
}

export interface PoItemRow {
  id: string | number
  /** Thông tin bảng tính của dòng giá đã chọn (ô nhập lúc mua, ánh xạ). */
  sheetInfo?: { has_sheet: boolean; input_names: string[]; mappings: Record<string, unknown>; result_cell: string | null } | null
  /** Số nhập lúc mua người dùng gõ, theo tên biến. */
  sheetInputs?: Record<string, string>
  /** Kết quả tính xem trước từ máy chủ (máy chủ tính lại khi lưu). */
  sheetPreview?: { purchase_price: number } | null
  sheetError?: string | null
  /** Lựa chọn quy cách theo cột KT/Định lượng (lọc dây chuyền). */
  sheetChoices?: SheetChoices
  material_id?: string
  name: string
  spec?: string
  desc?: string
  grade?: string
  t?: number | string
  w?: number | string
  l?: number | string
  uom: string
  qty: number | string
  price: number | null
  lineTotal: number
  notes: string
  isNegotiated: boolean
  priceSource?: 'auto' | 'manual' | 'SUPPLIER_QUOTE' | 'FIXED_PRICE' | 'MANUAL' | 'none'
  priceWarning?: string | null
  priceReason?: string | null
  priceRuleId?: string | null
  quoteId?: string | null
  quoteTitle?: string | null
  ruleItemName?: string | null
  density?: string | null
  quoteStatus?: string
  manualReason?: string | null
  priceBasisQty?: number
  candidates?: PoPriceCandidate[]
  [key: string]: any
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export const FREE_PO_VAT_RATE = 8

/** VAT PO chuẩn lấy từ NCC; dữ liệu cũ/thiếu VAT dùng mức mặc định đã chốt là 8%. */
export function supplierVatRateOf(value: unknown): number {
  const rate = finiteNumber(value)
  return rate == null ? FREE_PO_VAT_RATE : rate
}

/** KT4 và Định lượng có thể là chuỗi (ví dụ 30/15, 12kg); KT1-3 là số. */
export function sheetInputTypeForColumn(name: string): 'number' | 'text' {
  const normalized = stripDiacritics(name).trim().toLowerCase().replace(/\s+/g, ' ')
  return normalized === 'kt4' || normalized.includes('dinh luong') ? 'text' : 'number'
}

export function calculateFreePoTotals(rows: Array<Record<string, string | number | null>>, vatRate = FREE_PO_VAT_RATE): {
  quantity: number
  subtotal: number
  vat: number
  grandTotal: number
} {
  const subtotal = rows.reduce((sum, row) => {
    const qty = typeof row.qty === 'number' ? row.qty : parseNumberVi(row.qty)
    const price = typeof row.unit_price === 'number' ? row.unit_price : parseNumberVi(row.unit_price)
    return sum + (qty != null && price != null ? Math.round((qty * price + Number.EPSILON) * 100) / 100 : 0)
  }, 0)
  const quantity = rows.reduce((sum, row) => {
    const qty = typeof row.qty === 'number' ? row.qty : parseNumberVi(row.qty)
    return sum + (qty != null && qty > 0 ? qty : 0)
  }, 0)
  const vat = Math.round(subtotal * vatRate / 100)
  return { quantity, subtotal, vat, grandTotal: subtotal + vat }
}

function roundPoAmount(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000) / 1000
}

export function formatPoQuantity(value: number | string | null | undefined): string {
  const parsed = finiteNumber(value)
  if (parsed == null) return '0'
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 3,
    useGrouping: false,
  }).format(parsed)
}

export function sumPoLineTotals(items: Array<Pick<PoItemRow, 'lineTotal'>>): number {
  return items.reduce((sum, item) => sum + (finiteNumber(item.lineTotal) ?? 0), 0)
}

export function mapPoLineToEditableRow(line: PoLineDto): PoItemRow {
  const sheet = line.price_snapshot?.sheet
  const inputs = sheet?.inputs ?? {}
  const qty = finiteNumber(line.order_qty) ?? 0
  const price = line.price_source === 'UNPRICED' ? null : finiteNumber(line.unit_price_actual)
  const savedLineTotal = finiteNumber(line.line_total)
  const calculatedLineTotal = roundPoAmount(qty * (price ?? 0))
  const sheetPurchasePrice = finiteNumber(sheet?.purchase_price)
  const lineTotal = savedLineTotal != null && savedLineTotal > 0
    ? roundPoAmount(savedLineTotal)
    : calculatedLineTotal
  const sheetInfo: PoItemRow['sheetInfo'] = sheet ? {
    has_sheet: true,
    input_names: Object.keys(inputs),
    mappings: sheet.mappings ?? {},
    result_cell: sheet.result_cell ?? null,
  } : null
  const row: PoItemRow = {
    id: line.id,
    material_id: line.material_id,
    name: line.material_name ?? '',
    spec: line.spec_text ?? line.spec ?? '',
    desc: String(line.spec_attributes?.desc ?? ''),
    grade: line.quality ?? '',
    t: line.thick ?? '',
    w: line.width ?? '',
    l: line.length ?? '',
    uom: line.order_uom,
    qty,
    price,
    lineTotal,
    notes: String(line.spec_attributes?.reason ?? ''),
    isNegotiated: false,
    priceSource: line.price_source === 'UNPRICED' ? 'none' : line.price_source === 'MANUAL' ? 'manual' : 'auto',
    priceRuleId: line.price_rule_id ?? null,
    quoteId: sheet?.rule_id ?? null,
    quoteTitle: line.price_rule_label ?? null,
    manualReason: line.manual_reason ?? '',
    priceBasisQty: line.price_basis_qty ?? 1,
    priceUom: line.price_uom ?? line.order_uom,
    sheetInfo,
    sheetInputs: Object.fromEntries(Object.entries(inputs).map(([key, value]) => [key, String(value ?? '')])),
    sheetPreview: sheetPurchasePrice != null ? { purchase_price: sheetPurchasePrice } : null,
    quoteStatus: line.price_source === 'UNPRICED' ? 'PENDING' : undefined,
    priceWarning: line.price_source === 'UNPRICED' ? 'Chưa có giá' : null,
  }
  for (const [name, value] of Object.entries({ ...(sheet?.mappings ?? {}), ...inputs })) row[`sheet__${name}`] = value
  return row
}

export function requiresQuoteRuleForPoSave(
  row: Pick<PoItemRow, 'material_id' | 'priceRuleId' | 'price' | 'priceSource'>,
  hasSupplier: boolean,
  allowUnpriced = false,
): boolean {
  if (!hasSupplier || !row.material_id || row.priceRuleId) return false
  if (row.priceSource === 'manual' && Number(row.price) > 0) return false
  if (allowUnpriced && row.price == null) return false
  return true
}

export function capturePoItemSpec(item: PoItemRow, material: Pick<Material, 'attributes'> | undefined, format?: SupplierFormatDef, workbookInputs: PricingWorkbookInput[] = []) {
  const attrs: Record<string, unknown> = { ...material?.attributes, ...item.spec_attributes }
  const excluded = ['stt', 'id', 'name', 'spec', 'desc', 'qty', 'quantity', 'sl', 'uom', 'price', 'total', 'thanhtien', 'lineTotal', 't', 'w', 'l', 'grade', 'notes']
  for (const col of format?.columns ?? []) {
    if (!excluded.includes(col.key) && item[col.key] !== undefined && item[col.key] !== '') {
      attrs[col.canonical_key || col.key] = item[col.key]
      if (col.canonical_key === 'color' || /^(màu|màu sắc|colour|color)$/i.test(col.label.trim())) attrs.color = item[col.key]
    }
  }
  for (const key of ['density', 'kind', 'brand', 'finish', 'color', 'mau']) {
    if (item[key] !== undefined && item[key] !== '') attrs[key] = item[key]
  }
  const userInputs = workbookInputs.filter((input) => input.source === 'USER' || input.source === 'PO').reduce<Record<string, unknown>>((result, input) => {
    const raw = item[`wb__${input.key}`]
    if (raw === undefined || raw === null || raw === '') return result
    if (input.type === 'boolean') { result[input.key] = raw === true || raw === 'Có' || raw === 'true'; return result }
    if (input.type === 'number') {
      const value = Number(raw)
      if (Number.isFinite(value)) result[input.key] = value
      return result
    }
    result[input.key] = String(raw)
    return result
  }, {})
  if (Object.keys(userInputs).length) attrs.workbook_inputs = { ...((attrs.workbook_inputs as Record<string, unknown> | undefined) ?? {}), ...userInputs }
  if (item.mau && !item.color) attrs.color = item.mau
  return capturePoSpec(item.spec || item.desc, attrs, material?.attributes)
}

type PricingWorkbookInput = { key: string; label: string; type: 'number' | 'text' | 'boolean'; source: 'PO' | 'QUOTE' | 'USER'; unit?: string; required: boolean }

export type PoPricingSignatureInput = {
  supplierId: string | null | undefined
  materialId: string | null | undefined
  spec: string | null | undefined
  thick: number | string | null | undefined
  width: number | string | null | undefined
  length: number | string | null | undefined
  quality: string | null | undefined
  orderUom: string | null | undefined
  qty: number | string | null | undefined
  asOf: string | null | undefined
  selectedRuleId?: string | null | undefined
}

export function buildPoPricingSignature(input: PoPricingSignatureInput): string {
  const norm = (v: any) => (v === undefined || v === null ? '' : String(v).trim().toLowerCase())
  return [
    norm(input.supplierId),
    norm(input.materialId),
    norm(input.spec),
    norm(input.thick),
    norm(input.width),
    norm(input.length),
    norm(input.quality),
    norm(input.orderUom),
    norm(input.qty),
    norm(input.asOf),
    norm(input.selectedRuleId),
  ].join('|')
}

export type PoPricingCoordinatorCallbacks = {
  dispatchApi: (payload: any) => Promise<any>
  onApplyPrice: (rowId: string | number, res: any) => void
  onError: (rowId: string | number, err: any) => void
}

export class PoRowPricingCoordinator {
  private rowReqSeqMap = new Map<string | number, number>()
  private rowSignatureMap = new Map<string | number, string>()
  private rowTimerMap = new Map<string | number, ReturnType<typeof setTimeout>>()
  private callbacks: PoPricingCoordinatorCallbacks
  private debounceMs: number

  constructor(callbacks: PoPricingCoordinatorCallbacks, debounceMs: number = 300) {
    this.callbacks = callbacks
    this.debounceMs = debounceMs
  }

  setCallbacks(callbacks: PoPricingCoordinatorCallbacks) {
    this.callbacks = callbacks
  }

  getSeq(rowId: string | number): number {
    return this.rowReqSeqMap.get(rowId) || 0
  }

  getSignature(rowId: string | number): string | undefined {
    return this.rowSignatureMap.get(rowId)
  }

  invalidateRow(rowId: string | number): number {
    const existing = this.rowTimerMap.get(rowId)
    if (existing) {
      clearTimeout(existing)
      this.rowTimerMap.delete(rowId)
    }
    const nextSeq = (this.rowReqSeqMap.get(rowId) || 0) + 1
    this.rowReqSeqMap.set(rowId, nextSeq)
    this.rowSignatureMap.delete(rowId)
    return nextSeq
  }

  requestPrice(
    rowId: string | number,
    input: PoPricingSignatureInput,
    payload: any,
  ): { seq: number; signature: string } {
    const existing = this.rowTimerMap.get(rowId)
    if (existing) {
      clearTimeout(existing)
      this.rowTimerMap.delete(rowId)
    }

    const nextSeq = (this.rowReqSeqMap.get(rowId) || 0) + 1
    this.rowReqSeqMap.set(rowId, nextSeq)
    const signature = buildPoPricingSignature(input)
    this.rowSignatureMap.set(rowId, signature)

    const timer = setTimeout(async () => {
      if (this.rowReqSeqMap.get(rowId) !== nextSeq || this.rowSignatureMap.get(rowId) !== signature) {
        return
      }

      try {
        const res = await this.callbacks.dispatchApi(payload)
        if (this.rowReqSeqMap.get(rowId) !== nextSeq || this.rowSignatureMap.get(rowId) !== signature) {
          return
        }
        this.callbacks.onApplyPrice(rowId, res)
      } catch (err: any) {
        if (this.rowReqSeqMap.get(rowId) !== nextSeq || this.rowSignatureMap.get(rowId) !== signature) {
          return
        }
        this.callbacks.onError(rowId, err)
      }
    }, this.debounceMs)

    this.rowTimerMap.set(rowId, timer)
    return { seq: nextSeq, signature }
  }

  clear() {
    for (const timer of this.rowTimerMap.values()) {
      clearTimeout(timer)
    }
    this.rowTimerMap.clear()
    this.rowReqSeqMap.clear()
    this.rowSignatureMap.clear()
  }
}

export interface SignatureItem {
  role: string
  name: string
}

export const DEFAULT_PO_SIGNATURES: SignatureItem[] = [
  { role: 'BP ĐẶT HÀNG', name: 'ĐỨC TRÍ' },
  { role: 'KẾ HOẠCH', name: 'NGUYỄN VINH' },
  { role: 'BP KHO', name: 'VĂN THẮNG' },
  { role: 'BP KẾ TOÁN', name: 'MỸ THỤY' },
  { role: 'GIÁM ĐỐC', name: '' },
]


export function formatCurrency(val: number): string {
  return money(val)
}

export const DEFAULT_PO_COLUMNS: FormatColumnDef[] = [
  { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
  { key: 'name', label: 'TÊN HÀNG', width: 220, type: 'text', align: 'left' },
  { key: 'spec', label: 'QUY CÁCH / MÔ TẢ', width: 220, type: 'text', align: 'left' },
  { key: 'uom', label: 'ĐVT', width: 60, type: 'text', align: 'center' },
  { key: 'qty', label: 'SỐ LƯỢNG', width: 90, type: 'number', align: 'center' },
  { key: 'price', label: 'ĐƠN GIÁ (VNĐ)', width: 140, type: 'currency', align: 'right' },
  { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 140, type: 'currency', align: 'right' },
  { key: 'notes', label: 'GHI CHÚ', width: 180, type: 'text', align: 'left' },
]

/** Cột lấy từ báo giá dạng bảng của NCC (theo thứ tự trong báo giá). Khóa cột: sheet__<tên>. */
/** Thành tiền dòng bảng tính: SL × giá mua chính xác, làm tròn 3 số lẻ. */
/** Dòng bảng tính chờ người mua nhập đủ số để tự tính giá. */
export function sheetPendingWarning(names: string[] | undefined): string {
  return names && names.length > 0 ? 'Nhập ' + names.join(', ') : 'Đang tính giá từ bảng tính'
}

export function sheetLineTotalOf(qty: unknown, purchasePrice: number): number {
  const q = parseFloat(String(qty ?? '').replace(/,/g, '')) || 0
  return Math.round(q * purchasePrice * 1000) / 1000
}

/** Lý do chưa bấm Tính được (chọn dòng báo giá, thiếu số nhập); null nếu đủ điều kiện. */
export function sheetBlockReason(row: { priceRuleId?: string | null; sheetInfo?: { input_names: string[] } | null; sheetInputs?: Record<string, string> }): string | null {
  if (!row.priceRuleId) return 'Chọn dòng báo giá trước'
  const missing = (row.sheetInfo?.input_names ?? []).filter((n) => (row.sheetInputs?.[n] ?? '') === '')
  if (missing.length > 0) return 'Nhập ' + missing.join(', ')
  return null
}

/** Nhiều tên hàng có thể chung một vật tư: chỉ giữ dòng giá đúng tên hàng của dòng PO (không có dòng nào khớp tên thì giữ nguyên). */
export function filterRulesByItemName<T extends { supplier_item_name?: string | null }>(rules: T[], itemName: string | null | undefined): T[] {
  const key = (value: string) => stripDiacritics(value).trim().replace(/\s+/g, ' ')
  const wanted = key(itemName ?? '')
  if (!wanted) return rules
  const matched = rules.filter((rule) => key(rule.supplier_item_name ?? '') === wanted)
  return matched.length > 0 ? matched : rules
}

export function buildPoColumnsWithSheet(sheetCols: Array<{ name: string; locked: boolean }>): FormatColumnDef[] {
  const sheet = sheetCols.map((c): FormatColumnDef => ({ key: 'sheet__' + c.name, label: c.name, width: 110, type: 'text', align: 'right' }))
  return [
    { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
    { key: 'name', label: 'TÊN HÀNG', width: 220, type: 'text', align: 'left' },
    ...sheet,
    { key: 'uom', label: 'ĐVT', width: 60, type: 'text', align: 'center' },
    { key: 'qty', label: 'SỐ LƯỢNG', width: 90, type: 'number', align: 'center' },
    { key: 'price', label: 'ĐƠN GIÁ (VNĐ)', width: 140, type: 'currency', align: 'right' },
    { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 140, type: 'currency', align: 'right' },
    { key: 'notes', label: 'GHI CHÚ', width: 180, type: 'text', align: 'left' },
  ]
}

export function buildPoColumns(format: SupplierFormatDef | undefined): FormatColumnDef[] {
  if (!format || !format.columns || format.columns.length === 0) {
    return DEFAULT_PO_COLUMNS
  }

  let mappedIdentity = false
  const cols = format.columns.map((c) => {
    if (c.role === 'identity' && !mappedIdentity) { mappedIdentity = true; return { ...c, key: 'name' } }
    if (c.canonical_key === 'price') return { ...c, key: 'price' }
    if (c.canonical_key === 'uom') return { ...c, key: 'uom' }
    return { ...c }
  })

  // 1. Ensure STT column at the beginning
  const sttIdx = cols.findIndex((c) => c.key === 'stt' || c.key === 'id')
  if (sttIdx === -1) {
    cols.unshift({ key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' })
  } else if (sttIdx > 0) {
    const [sttCol] = cols.splice(sttIdx, 1)
    cols.unshift(sttCol)
  }

  // 2. Ensure qty column exists
  const hasQty = cols.some((c) => c.key === 'qty' || c.key === 'quantity' || c.key === 'sl')
  if (!hasQty) {
    const priceIdx = cols.findIndex((c) => c.key === 'price')
    const qtyCol: FormatColumnDef = { key: 'qty', label: 'SỐ LƯỢNG', width: 85, type: 'number', align: 'center' }
    if (priceIdx >= 0) {
      cols.splice(priceIdx, 0, qtyCol)
    } else {
      cols.push(qtyCol)
    }
  }

  // 3. Ensure price column exists
  const priceIdx = cols.findIndex((c) => c.key === 'price')
  if (priceIdx === -1) {
    cols.push({ key: 'price', label: 'ĐƠN GIÁ (VNĐ)', width: 135, type: 'currency', align: 'right' })
  }

  // 4. Ensure total column exists
  const hasTotal = cols.some((c) => c.key === 'total' || c.key === 'thanhtien' || c.key === 'lineTotal')
  if (!hasTotal) {
    const curPriceIdx = cols.findIndex((c) => c.key === 'price')
    const totalCol: FormatColumnDef = { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 135, type: 'currency', align: 'right' }
    if (curPriceIdx >= 0) {
      cols.splice(curPriceIdx + 1, 0, totalCol)
    } else {
      cols.push(totalCol)
    }
  }

  // 5. Ensure notes column exists
  const hasNotes = cols.some((c) => c.key === 'notes' || c.key === 'note')
  if (!hasNotes) {
    cols.push({ key: 'notes', label: 'GHI CHÚ', width: 170, type: 'text', align: 'left' })
  }

  return cols
}

export function normalizeItemName(s: string): string {
  return s
    .normalize('NFC')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

export function createEmptyRow(
  type: PoQuoteType,
  format?: SupplierFormatDef,
  rules?: PriceRule[],
  quotesMap?: Map<string, SupplierQuote>,
): PoItemRow {
  const isWood =
    type === 'van_cao_su' || type === 'phoi_go' || type === 'van_ep' || type === 'veneer'
  const fmt = format || (type ? supplierFormatService.getById(type) : undefined)
  const defaultUom =
    fmt?.uomDefault ||
    (type === 'phoi_go' ? 'm³' : type === 'veneer' ? 'm²' : isWood ? 'Tấm' : 'Pcs')
  const row: PoItemRow = {
    id: Date.now() + Math.random(),
    name: '',
    spec: '',
    desc: '',
    grade: '',
    t: '',
    w: type === 'van_ep' ? 1220 : '',
    l: type === 'van_ep' ? 2440 : '',
    uom: defaultUom,
    qty: '',
    price: null,
    lineTotal: 0,
    priceSource: 'none',
    notes: '',
    isNegotiated: false,
  }

  if (fmt?.columns) {
    for (const c of fmt.columns) {
      if (['stt', 'id', 'price', 'total', 'thanhtien', 'lineTotal'].includes(c.key)) continue
      if (c.key === 'grade') {
        const gradeOpts = c.options || ['AB', 'AC', 'BC', 'CC']
        row.grade = gradeOpts.length === 1 ? gradeOpts[0] : ''
      } else if (c.type === 'select' && c.options) {
        row[c.key] = c.options.length === 1 ? c.options[0] : ''
      } else if (row[c.key] === undefined) {
        row[c.key] = ''
      }
    }
  }

  if (rules && rules.length > 0) {
    const filled = autoFillFromQuote(row, rules, quotesMap || new Map(), fmt)
    Object.assign(row, filled)
  }

  return row
}

/**
 * Trích xuất thuộc tính từ PriceRule theo key.
 */
function extractRuleAttr(r: PriceRule, key: string, canonicalKey?: string | null): unknown {
  const spec = (r.spec_attributes || {}) as Record<string, unknown>
  const orig = (r.original_values || {}) as Record<string, unknown>

  if (canonicalKey === 'supplier_item_name') return r.supplier_item_name || spec.name || orig.name || ''
  if (canonicalKey === 'price') return r.unit_price
  if (canonicalKey === 'uom') return r.uom || spec.uom || orig.uom || ''

  switch (key) {
    case 'name':
      return r.supplier_item_name || spec.name || spec.kind || orig.name || ''
    case 'supplier_item_name':
      return r.supplier_item_name || spec.name || orig.name || ''
    case 'price':
      return r.unit_price
    case 'uom':
      return r.uom || spec.uom || orig.uom || ''
    case 'kind':
      return spec.kind || orig.kind || r.supplier_item_name || ''
    case 'spec':
      return spec.spec || orig.spec || spec.qc || spec.kt1 || (r as any).spec_text || ''
    case 'density':
    case 'dinh_luong':
      return spec.density || orig.density || spec.dinh_luong || orig.dinh_luong || ''
    case 'uom':
      return r.uom || spec.uom || orig.uom || ''
    case 'grade':
      return (
        r.quality_grade ||
        r.quality_code ||
        r.quality_raw ||
        spec.quality ||
        spec.grade ||
        ''
      )
    case 't':
      return spec.thickness ?? spec.thick ?? spec.t
    case 'w':
      return spec.width ?? spec.w
    case 'l':
      return spec.length ?? spec.l
    default:
      return spec[canonicalKey || key] ?? spec[key] ?? orig[canonicalKey || key] ?? orig[key] ?? (r as any)[key]
  }
}

/**
 * Auto-fill các field phụ thuộc từ báo giá NCC khi có DUY NHẤT 1 lựa chọn.
 * Không ghi đè các field người dùng đã tự nhập tay.
 * Không đụng vào field giá (price).
 */
export function autoFillFromQuote(
  partialRow: Partial<PoItemRow>,
  rules: PriceRule[],
  quotesMap: Map<string, SupplierQuote>,
  format?: SupplierFormatDef,
  ignoredKeys: string[] = [],
): Partial<PoItemRow> {
  if (!rules || rules.length === 0) {
    return { ...partialRow }
  }

  const today = new Date().toISOString().slice(0, 10)
  const activeRules = rules.filter((r) => {
    if (r.status !== 'ACTIVE') return false
    if (quotesMap && quotesMap.size > 0 && r.quote_id) {
      const q = quotesMap.get(r.quote_id)
      if (!q || q.status !== 'ACTIVE') return false
      if (q.effective_from && q.effective_from > today) return false
      if (q.effective_until && q.effective_until < today) return false
    }
    return true
  })

  if (activeRules.length === 0) {
    return { ...partialRow }
  }

  const out: Partial<PoItemRow> = { ...partialRow }

  // Lọc candidate rules theo các field mà out ĐÃ CÓ giá trị
  const filterCandidates = (current: Partial<PoItemRow>): PriceRule[] => {
    const rawName = (current.name || current.kind || '').trim()
    const rowNormName = rawName ? normalizeItemName(rawName) : ''
    const rowGrade = (current.grade || '').trim().toUpperCase()
    const rowSpec = (current.spec || '').trim()
    const rowT = current.t !== undefined && current.t !== '' ? Number(current.t) : undefined
    const rowW = current.w !== undefined && current.w !== '' ? Number(current.w) : undefined
    const rowL = current.l !== undefined && current.l !== '' ? Number(current.l) : undefined

    return activeRules.filter((r) => {
      const spec = (r.spec_attributes || {}) as Record<string, unknown>
      const orig = (r.original_values || {}) as Record<string, unknown>

      // 1. Khớp tên / loại
      if (rowNormName) {
        const rName = normalizeItemName(r.supplier_item_name || '')
        const specName = normalizeItemName(String(spec.name || ''))
        const specKind = normalizeItemName(String(spec.kind || orig.kind || ''))
        const match =
          (rName && rName === rowNormName) ||
          (specName && specName === rowNormName) ||
          (specKind && specKind === rowNormName)
        if (!match) return false
      }

      // 2. Khớp grade
      if (rowGrade) {
        const ruleGrade = String(
          r.quality_grade || r.quality_code || r.quality_raw || spec.quality || spec.grade || '',
        )
          .trim()
          .toUpperCase()
        if (ruleGrade && ruleGrade !== rowGrade) return false
      }

      // 3. Khớp spec
      if (rowSpec) {
        const rSpec = String(spec.spec || orig.spec || spec.qc || spec.kt1 || (r as any).spec_text || '').trim()
        if (rSpec && normalizeItemName(rSpec) !== normalizeItemName(rowSpec)) return false
      }

      // Mọi cột lookup đã khai báo phải khớp rõ theo khóa chuẩn, không coi ô trống của dòng giá mới là wildcard.
      for (const col of format?.columns ?? []) {
        if (col.role !== 'lookup') continue
        const lookupKey = col.canonical_key || col.key
        const wanted = current[col.key] ?? current[lookupKey]
        if (wanted === undefined || wanted === null || wanted === '') continue
        const actual = spec[lookupKey] ?? spec[col.key] ?? orig[lookupKey] ?? orig[col.key]
        if (actual === undefined || actual === null || actual === '') return false
        const same = typeof wanted === 'number' || typeof actual === 'number'
          ? Number(wanted) === Number(actual)
          : normalizeItemName(String(wanted)) === normalizeItemName(String(actual))
        if (!same) return false
      }

      // 4. Khớp kích thước
      if (rowT !== undefined && !isNaN(rowT)) {
        const ruleT = spec.thickness ?? spec.thick ?? spec.t
        if (ruleT != null && Number(ruleT) !== rowT) return false
      }
      if (rowW !== undefined && !isNaN(rowW)) {
        const ruleW = spec.width ?? spec.w
        if (ruleW != null && Number(ruleW) !== rowW) return false
      }
      if (rowL !== undefined && !isNaN(rowL)) {
        const ruleL = spec.length ?? spec.l
        if (ruleL != null && Number(ruleL) !== rowL) return false
      }

      return true
    })
  }

  let candidates = filterCandidates(out)
  if (candidates.length === 0) {
    return out
  }

  // Danh sách các key cần xem xét auto-fill
  const candidateKeys: string[] = []
  if (format?.columns) {
    for (const c of format.columns) {
      if (
        ![
          'stt',
          'id',
          'qty',
          'quantity',
          'sl',
          'price',
          'total',
          'thanhtien',
          'lineTotal',
          'vol',
          'area',
          'notes',
          'note',
        ].includes(c.key)
      ) {
        candidateKeys.push(c.key)
      }
    }
  } else {
    candidateKeys.push('name', 'kind', 'spec', 'density', 'grade', 't', 'w', 'l', 'uom')
  }

  // Áp dụng auto-fill (lặp tối đa 3 lần để cascade các quan hệ phụ thuộc)
  let changed = true
  let iterations = 0
  while (changed && iterations < 3) {
    changed = false
    iterations++
    candidates = filterCandidates(out)
    if (candidates.length === 0) break

    for (const key of candidateKeys) {
      if (ignoredKeys.includes(key)) continue

      const curVal = out[key]
      const isEmpty =
        curVal === undefined ||
        curVal === null ||
        curVal === '' ||
        (key === 'uom' && curVal === format?.uomDefault)
      if (!isEmpty) continue

      const rawVals = candidates
        .map((r) => extractRuleAttr(r, key, format?.columns.find((column) => column.key === key)?.canonical_key))
        .filter((v) => v !== undefined && v !== null && v !== '')

      const distinct = Array.from(
        new Set(
          rawVals.map((v) => (typeof v === 'string' ? v.trim() : v)),
        ),
      )

      if (distinct.length === 1) {
        out[key] = distinct[0]
        if (key === 'kind' && (!out.name || out.name === '')) {
          if (!ignoredKeys.includes('name')) {
            out.name = String(distinct[0])
          }
        } else if (key === 'name' && (!out.kind || out.kind === '')) {
          if (!ignoredKeys.includes('kind')) {
            out.kind = String(distinct[0])
          }
        }
        changed = true
      }
    }
  }

  return out
}

export function PoCreatePageNew() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { poId: editPoId } = useParams<{ poId?: string }>()
  const isPoEditMode = Boolean(editPoId)
  const [searchParams] = useSearchParams()
  const preselectedSupplierId = searchParams.get('supplierId')

  // Free PO states
  const [isFreePo, setIsFreePo] = useState(() => {
    return searchParams.get('free') === '1' || searchParams.get('free') === 'true'
  })
  const [freeColumns, setFreeColumns] = useState<FreeColumn[]>(() => [...CORE_COLUMNS])
  const [freeRows, setFreeRows] = useState<FreeRow[]>(makeInitialFreeRows)
  const [freeVatRate, setFreeVatRate] = useState(String(FREE_PO_VAT_RATE))
  const [standardVatRate, setStandardVatRate] = useState('8')

  useEffect(() => {
    const freeParam = searchParams.get('free')
    if (freeParam === '1' || freeParam === 'true') {
      setIsFreePo(true)
    }
  }, [searchParams])

  const computeFreeRow = useCallback((row: FreeRow): FreeRow => {
    const q = typeof row.qty === 'number' ? row.qty : parseNumberVi(row.qty)
    const p = typeof row.unit_price === 'number' ? row.unit_price : parseNumberVi(row.unit_price)
    if (q != null && p != null) {
      const total = Math.round((q * p + Number.EPSILON) * 100) / 100
      return { ...row, line_total: total }
    }
    return { ...row, line_total: 0 }
  }, [])

  const parsedFreeVatRate = parseNumberVi(freeVatRate) ?? 0
  const freeTotals = useMemo(() => calculateFreePoTotals(freeRows, parsedFreeVatRate), [freeRows, parsedFreeVatRate])
  const freeTotalQty = freeTotals.quantity
  const freeSubTotal = freeTotals.subtotal
  const freeVatAmount = freeTotals.vat
  const freeGrandTotal = freeTotals.grandTotal

  // Masters
  const [systemSuppliers, setSystemSuppliers] = useState<Supplier[]>([])
  const [systemMaterials, setSystemMaterials] = useState<Material[]>([])
  const [loading, setLoading] = useState(Boolean(editPoId))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  const [editPoOriginal, setEditPoOriginal] = useState<PurchaseOrderDto | null>(null)

  // Document State - defaults to 'Nháp' (mới tạo chưa gửi NCC)
  const [status, setStatus] = useState<
    'Nháp' | 'Đã Gửi NCC' | 'Nhận 1 Phần' | 'Đã Nhận Hàng' | 'Hủy Đơn Hàng'
  >('Nháp')
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(true)
  const [poHeaderCollapsed, setPoHeaderCollapsed] = useState(false)

  // Header Info - Clean start (no fake dummy data)
  const [poNumber, setPoNumber] = useState(() => {
    const d = new Date()
    const yy = String(d.getFullYear()).slice(-2)
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const rand = Math.floor(Math.random() * 900) + 100
    return `PO-${yy}${mm}-${rand}`
  })
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
  const [supplierInputName, setSupplierInputName] = useState('')
  const [supplierAddress, setSupplierAddress] = useState('')
  const [supplierContact, setSupplierContact] = useState('')
  const [supplierPhone, setSupplierPhone] = useState('')
  const [workshop, setWorkshop] = useState('XƯỞNG 1')
  const [deliveryDate, setDeliveryDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 4)
    return d.toISOString().slice(0, 10)
  })
  const [orderNotes, setOrderNotes] = useState('')
  const quoteType: PoQuoteType = ''
  const [supplierRules, setSupplierRules] = useState<PriceRule[]>([])
  const [hiddenPoColumns, setHiddenPoColumns] = useState<string[]>([])
  const [columnMenuOpen, setColumnMenuOpen] = useState(false)

  const poHiddenColumnsKey = `erp.grid.po.${quoteType || 'default'}.hidden.${user?.id ?? 'anonymous'}`
  // Mặc định ẩn 4 cột cuối (ĐVT tính giá, ĐVT tồn kho, Giá cho N đơn vị, Cách tính / nguồn giá).
  // Dữ liệu và snapshot vẫn giữ nguyên; người dùng bật lại trong menu Cột khi cần.
  const defaultHiddenPoColumns = ['priceUomDisplay', 'stockUomDisplay', 'priceBasisQty', 'calculation']
  useEffect(() => {
    try {
      const saved = localStorage.getItem(poHiddenColumnsKey)
      if (saved === null) {
        setHiddenPoColumns(defaultHiddenPoColumns)
        return
      }
      const parsed = JSON.parse(saved)
      setHiddenPoColumns(Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : defaultHiddenPoColumns)
    } catch { setHiddenPoColumns(defaultHiddenPoColumns) }
  }, [poHiddenColumnsKey])

  const [poSheetColumns, setPoSheetColumns] = useState<Array<{ name: string; locked: boolean }>>([])
  useEffect(() => {
    const supplierKey = selectedSupplier?.id
    if (!supplierKey) {
      setPoSheetColumns([])
      return
    }
    let cancelled = false
    apiFetch('/api/suppliers/' + encodeURIComponent(supplierKey) + '/po-columns')
      .then(async (res) => {
        const body = (await res.json()) as { ok?: boolean; columns?: Array<{ name: string; locked: boolean }> | null }
        if (!cancelled) setPoSheetColumns(body.columns ?? [])
      })
      .catch(() => { if (!cancelled) setPoSheetColumns([]) })
    return () => { cancelled = true }
  }, [selectedSupplier?.id])

  const tableColumns = useMemo(() => {
    return [...(poSheetColumns.length > 0 ? buildPoColumnsWithSheet(poSheetColumns) : buildPoColumns(undefined)),
    { key: 'priceUomDisplay', label: 'ĐVT TÍNH GIÁ', width: 90, type: 'text', align: 'center' },
    { key: 'stockUomDisplay', label: 'ĐVT TỒN KHO', width: 90, type: 'text', align: 'center' },
    { key: 'priceBasisQty', label: 'GIÁ CHO N ĐƠN VỊ', width: 110, type: 'number', align: 'right' },
    { key: 'calculation', label: 'CÁCH TÍNH / NGUỒN GIÁ', width: 140, type: 'text', align: 'left' },
    ] as FormatColumnDef[]
  }, [poSheetColumns])

  const visibleTableColumns = useMemo(() => tableColumns.filter((column) => column.key.startsWith('sheet__') || !hiddenPoColumns.includes(column.key)), [tableColumns, hiddenPoColumns])

  const totalColumnWeights = useMemo(() => {
    const sum = visibleTableColumns.reduce((acc, c) => acc + (c.width || 100), 0)
    return isEditMode ? sum + 38 : sum
  }, [visibleTableColumns, isEditMode])

  const totalColIdx = useMemo(() => {
    const idx = visibleTableColumns.findIndex(
      (c) => c.key === 'total' || c.key === 'thanhtien' || c.key === 'lineTotal',
    )
    return idx >= 0 ? idx : Math.max(1, tableColumns.length - 1)
  }, [visibleTableColumns])

  const firstSummaryColIdx = useMemo(() => {
    const idx = visibleTableColumns.findIndex(
      (c) =>
        c.key === 'qty' ||
        c.key === 'quantity' ||
        c.key === 'sl' ||
        c.key === 'vol' ||
        c.key === 'area',
    )
    return idx >= 0 ? idx : totalColIdx
  }, [visibleTableColumns, totalColIdx])

  const freePrintColumnWeights = useMemo(() => freeColumns.map((column) => {
    if (column.key === 'name') return 220
    if (column.key === 'note') return 180
    if (column.key === 'unit_price' || column.key === 'line_total') return 140
    if (column.key === 'qty') return 90
    if (column.key === 'uom') return 60
    return column.width || 120
  }), [freeColumns])
  const freePrintTotalWeight = useMemo(() => 48 + freePrintColumnWeights.reduce((sum, weight) => sum + weight, 0), [freePrintColumnWeights])
  const freePrintAmountIndex = useMemo(() => 1 + Math.max(0, freeColumns.findIndex((column) => column.key === 'line_total')), [freeColumns])
  const freePrintTrailingColumns = Math.max(0, freeColumns.length + 1 - freePrintAmountIndex - 1)

  // Table Rows - Starts with 1 clean empty row
  const [items, setItems] = useState<PoItemRow[]>([createEmptyRow('', undefined)])
  const sheetCalcTimersRef = useRef(new Map<string | number, ReturnType<typeof setTimeout>>())
  useEffect(() => () => {
    for (const timer of sheetCalcTimersRef.current.values()) clearTimeout(timer)
    sheetCalcTimersRef.current.clear()
  }, [])

  /** Dòng giá ACTIVE của từng vật tư đang có trên PO (NCC có báo giá bảng tính), cache theo "NCC|vật tư". */
  const [sheetRulesByKey, setSheetRulesByKey] = useState<Record<string, ActiveRuleOption[]>>({})
  useEffect(() => {
    const supplierKey = selectedSupplier?.id
    if (!supplierKey) return
    const mats = Array.from(new Set(items.map((i) => i.material_id).filter((m): m is string => Boolean(m))))
    for (const mat of mats) {
      const key = supplierKey + '|' + mat
      if (sheetRulesByKey[key]) continue
      apiFetch('/api/suppliers/' + encodeURIComponent(supplierKey) + '/active-rules?item_id=' + encodeURIComponent(mat) + '&limit=200')
        .then(async (res) => {
          const body = (await res.json()) as { ok?: boolean; rules?: ActiveRuleOption[] }
          setSheetRulesByKey((prev) => ({ ...prev, [key]: body.ok ? body.rules ?? [] : [] }))
        })
        .catch(() => setSheetRulesByKey((prev) => ({ ...prev, [key]: [] })))
    }
  }, [items, selectedSupplier?.id, poSheetColumns, sheetRulesByKey])

  /** Tự điền ô chỉ còn một giá trị; tổ hợp khớp đúng một dòng thì gắn dòng đó (đơn giá theo dòng). */
  useEffect(() => {
    const supplierKey = selectedSupplier?.id
    if (!supplierKey) return
    const names = poSheetColumns.map((c) => c.name)
    items.forEach((row, index) => {
      if (!row.material_id || row.priceSource === 'manual') return
      const allRules = sheetRulesByKey[supplierKey + '|' + row.material_id]
      const rules = allRules ? filterRulesByItemName(allRules, row.name) : allRules
      if (!rules || rules.length === 0) return
      const inputs = inputNamesOf(rules, row.sheetInfo?.input_names ?? [])
      const choices: SheetChoices = { ...(row.sheetChoices ?? {}) }
      let changed = false
      for (const name of names) {
        if (choices[name] || inputs.has(name)) continue
        const opts = sheetOptionsFor(rules, choices, name, names)
        if (opts.length === 1) {
          choices[name] = opts[0]
          changed = true
        }
      }
      if (changed) {
        setItems((prev) => {
          const copy = [...prev]
          if (copy[index] && copy[index].id === row.id) copy[index] = { ...copy[index], sheetChoices: choices }
          return copy
        })
        return
      }
      const matching = sheetMatchingRules(rules, choices)
      if (matching.length === 1 && matching[0].id !== row.priceRuleId) {
        handleSelectCandidateRule(index, matching[0].id)
      }
    })
  }, [items, sheetRulesByKey, selectedSupplier?.id, poSheetColumns]) // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedRowIdx, setSelectedRowIdx] = useState<number>(0)
  const [activeMenuRowIdx, setActiveMenuRowIdx] = useState<number | null>(null)
  const [rowMenuPos, setRowMenuPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null)
  const [calculationOpen, setCalculationOpen] = useState(false)
  const [calculationSnapshot, setCalculationSnapshot] = useState<PriceSnapshot | null>(null)
  const [calculationLoading, setCalculationLoading] = useState(false)
  const [calculationError, setCalculationError] = useState<string | null>(null)
  const calculationSequence = useRef(0)

  async function showCalculation(item: PoItemRow) {
    const sequence = ++calculationSequence.current
    setCalculationOpen(true)
    setCalculationSnapshot(null)
    setCalculationError(null)
    setCalculationLoading(true)
    try {
      if (!selectedSupplier?.id || !item.material_id) throw new Error('Chọn nhà cung cấp và vật tư để xem cách tính')
      const material = systemMaterials.find(m => m.id === item.material_id)
      const captured = capturePoItemSpec(item, material)
      const result = await previewPrice({
        supplier_id: selectedSupplier.id, material_id: item.material_id,
        order_qty: Number(item.qty), order_uom: item.uom,
        spec_text: captured.spec, spec_attributes: captured.spec_attributes,
        thick: item.t === '' ? undefined : Number(item.t) || undefined,
        width: item.w === '' ? undefined : Number(item.w) || undefined,
        length: item.l === '' ? undefined : Number(item.l) || undefined,
        quality: item.grade, as_of: deliveryDate,
        selected_rule_id: item.priceRuleId,
        manual_price: item.priceSource === 'manual' ? item.price : undefined,
        manual_reason: item.priceSource === 'manual' ? item.manualReason || 'Nhập tay theo thỏa thuận' : undefined,
        price_basis_qty: Number(item.priceBasisQty ?? 1),
        price_uom: item.priceUom || undefined,
      })
      if (sequence !== calculationSequence.current) return
      if (!result.price_snapshot) throw new Error('Máy chủ chưa trả snapshot cách tính cho dòng xem trước')
      setCalculationSnapshot(result.price_snapshot)
    } catch (err) {
      if (sequence === calculationSequence.current) setCalculationError(err instanceof Error ? err.message : 'Không thể tải cách tính')
    } finally { if (sequence === calculationSequence.current) setCalculationLoading(false) }
  }

  const totalArea = useMemo(() => {
    return items.reduce((sum, it) => {
      const width = Number(it.w || 0)
      const length = Number(it.l || 0)
      const quantity = parseFloat(String(it.qty || 0).replace(/,/g, '')) || 0
      if (![width, length, quantity].every(Number.isFinite)) return sum
      return sum + (width * length * quantity) / 1000000
    }, 0)
  }, [items])

  const totalVol = useMemo(() => {
    return items.reduce((sum, it) => {
      const thickness = Number(it.t || 0)
      const width = Number(it.w || 0)
      const length = Number(it.l || 0)
      const quantity = parseFloat(String(it.qty || 0).replace(/,/g, '')) || 0
      if (![thickness, width, length, quantity].every(Number.isFinite)) return sum
      return sum + (thickness * width * length * quantity) / 1000000000
    }, 0)
  }, [items])

  // Paste modal
  const [pasteModalOpen, setPasteModalOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')

  // 5 standard signatures. Director name is intentionally blank until a configured
  // DIRECTOR signer is available; the signature slot must still be present in every PO export.
  const [signatures, setSignatures] = useState<SignatureItem[]>(DEFAULT_PO_SIGNATURES)
  const poSignersBaselineRef = useRef<PoSigner[]>(DEFAULT_PO_SIGNERS.map((signer) => ({ ...signer })))

  useEffect(() => {
    if (isPoEditMode) return
    let cancelled = false
    void (async () => {
      const local = readDefaultPoSigners()
      try {
        const server = await getPoSignersSettings()
        if (cancelled) return
        const selected = server.configured ? server.signers : (local ?? DEFAULT_PO_SIGNERS)
        setSignatures(selected.map((s) => ({ role: s.title, name: s.name })))
        poSignersBaselineRef.current = selected.map((s) => ({ ...s }))
        if (!server.configured && local) {
          try { await savePoSignersSettings(local) } catch { /* keep local fallback when offline */ }
        }
      } catch {
        if (local && !cancelled) {
          setSignatures(local.map((s) => ({ role: s.title, name: s.name })))
          poSignersBaselineRef.current = local.map((s) => ({ ...s }))
        }
      }
    })()
    return () => { cancelled = true }
  }, [isPoEditMode])

  const selectedSupplierRef = useRef<Supplier | null>(selectedSupplier)
  useEffect(() => {
    selectedSupplierRef.current = selectedSupplier
  }, [selectedSupplier])

  const coordinatorRef = useRef<PoRowPricingCoordinator | null>(null)
  if (!coordinatorRef.current) {
    coordinatorRef.current = new PoRowPricingCoordinator(
      {
        dispatchApi: (payload) => resolvePriceApi(payload),
        onApplyPrice: () => {},
        onError: () => {},
      },
      300,
    )
  }

  useEffect(() => {
    return () => {
      coordinatorRef.current?.clear()
    }
  }, [])

  useEffect(() => {
    coordinatorRef.current?.setCallbacks({
      dispatchApi: (payload) => resolvePriceApi(payload),
      onApplyPrice: (rowId, res) => {
        setItems((currentItems) => {
          const idx = currentItems.findIndex((it) => it.id === rowId)
          if (idx === -1) return currentItems
          const current = currentItems[idx]
          if (current.priceSource === 'manual') return currentItems
          if (selectedSupplierRef.current?.id && res.supplierId && selectedSupplierRef.current.id !== res.supplierId) return currentItems

          const updated = { ...current }
          if (res.status === 'MATCHED' && res.unitPrice != null && res.lineTotal != null) {
            updated.price = res.unitPrice
            updated.priceSource = 'SUPPLIER_QUOTE'
            updated.priceRuleId = res.ruleId || res.priceRuleId || null
            updated.quoteId = res.quoteId
            updated.priceWarning = null
            updated.priceReason = res.reason || 'Khớp theo báo giá NCC'
            updated.ruleItemName = res.ruleItemName || null
            updated.quoteTitle = res.quoteTitle
            updated.quoteStatus = 'MATCHED'
            // Authoritative server lineTotal - strictly from server, NO client fallback
            updated.lineTotal = res.lineTotal
            updated.priceUom = res.uom
          } else if (res.status === 'MATCHED' && res.lineTotal == null) {
            // Missing lineTotal contract error
            updated.price = null
            updated.priceSource = 'none'
            updated.priceRuleId = null
            updated.quoteId = null
            updated.quoteStatus = 'ERROR'
            updated.priceWarning = 'Lỗi máy chủ: Thiếu thông tin thành tiền (lineTotal)'
            updated.priceReason = 'Lỗi máy chủ: Thiếu thông tin thành tiền (lineTotal)'
            updated.lineTotal = 0
            updated.candidates = undefined
          } else {
            updated.price = null
            updated.priceSource = 'none'
            updated.priceRuleId = null
            updated.quoteId = null
            updated.quoteStatus = res.status
            updated.priceWarning = res.reason || res.status
            updated.priceReason = res.reason
            updated.lineTotal = 0
            updated.candidates = res.candidates?.map((c: any) => ({
              ruleId: c.rule_id,
              unitPrice: c.unit_price,
              ruleItemName: c.label,
              quoteTitle: res.quoteTitle || 'Báo giá',
              uom: c.uom,
            }))
          }

          const copy = [...currentItems]
          copy[idx] = updated
          return copy
        })
      },
      onError: (rowId, err) => {
        setError(`Lỗi giải quyết giá: ${err?.message || 'Lỗi hệ thống'}`)
        setItems((currentItems) => {
          const idx = currentItems.findIndex((it) => it.id === rowId)
          if (idx === -1) return currentItems
          const current = currentItems[idx]
          if (current.priceSource === 'manual') return currentItems

          const updated = { ...current }
          updated.price = null
          updated.priceSource = 'none'
          updated.priceRuleId = null
          updated.quoteId = null
          updated.quoteStatus = 'ERROR'
          updated.priceWarning = `Lỗi máy chủ: ${err?.message || 'Không thể tra cứu giá'}`
          updated.priceReason = null
          updated.lineTotal = 0
          updated.candidates = undefined

          const copy = [...currentItems]
          copy[idx] = updated
          return copy
        })
      },
    })
  })

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!(e.target as HTMLElement).closest('.row-menu-container')) {
        setActiveMenuRowIdx(null)
      }
      if (!(e.target as HTMLElement).closest('.status-menu-container')) {
        setStatusMenuOpen(false)
      }
    }
    const closeRowMenu = () => setActiveMenuRowIdx(null)
    document.addEventListener('click', handleClickOutside)
    window.addEventListener('scroll', closeRowMenu, true)
    window.addEventListener('resize', closeRowMenu)
    return () => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('scroll', closeRowMenu, true)
      window.removeEventListener('resize', closeRowMenu)
    }
  }, [])

  function showToast(msg: string) {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(null), 3500)
  }

  async function persistNewPoSignersDefault() {
    const current = signatures.map((signer) => ({ title: signer.role, name: signer.name }))
    if (!shouldAutoSavePoSignersDefault(isPoEditMode, current, poSignersBaselineRef.current)) return
    try { await savePoSignersSettings(current) } catch { /* local fallback remains available when the server is offline */ }
    try { localStorage.setItem('po.signers.default.v1', JSON.stringify(current)) } catch { /* storage unavailable */ }
    poSignersBaselineRef.current = current
  }

  // Load masters from API/Services
  useEffect(() => {
    let cancelled = false
    async function loadMasters() {
      try {
        const [sups, mats] = await Promise.all([
          supplierService.list(),
          materialService.list(),
        ])
        if (cancelled) return
        setSystemSuppliers(sups.filter((s) => s.status === 'ACTIVE'))
        setSystemMaterials(mats)

        if (editPoId) {
          const po = await getPurchaseOrder(editPoId)
          if (cancelled) return
          if (!po) throw new Error('Không tìm thấy PO cần sửa.')
          if (po.status !== 'DRAFT') {
            navigate(`/purchasing/po/${encodeURIComponent(editPoId)}`, { replace: true })
            return
          }
          const supplier = sups.find((candidate) => candidate.id === po.supplier_id)
          if (!supplier) throw new Error('Không tìm thấy nhà cung cấp của PO này.')
          setEditPoOriginal(po)
          if (Array.isArray(po.signers) && po.signers.length > 0) setSignatures(po.signers.map((s) => ({ role: s.title, name: s.name })))
          setSelectedSupplier(supplier)
          setSupplierInputName(supplier.name)
          setStandardVatRate(String((po as any).vat_rate ?? supplier.default_vat_rate ?? 8))
          setSupplierAddress(supplier.address || '')
          setSupplierContact(supplier.contact_person || '')
          setSupplierPhone(supplier.phone || '')
          setPoNumber(po.code)
          setDeliveryDate(po.delivery_date || '')
          setWorkshop(po.delivery_site || 'XƯỞNG 1')
          setOrderNotes(po.note || po.notes || '')
          if ((po as any).kind === 'FREE') {
            setIsFreePo(true)
            setFreeVatRate(String((po as any).vat_rate ?? FREE_PO_VAT_RATE))
            if (Array.isArray((po as any).free_layout) && (po as any).free_layout.length > 0) {
              setFreeColumns((po as any).free_layout)
            }
            if (Array.isArray((po as any).free_lines) && (po as any).free_lines.length > 0) {
              setFreeRows(
                (po as any).free_lines.map((l: any) =>
                  normalizeFreeLine(l, (po as any).free_layout),
                ),
              )
            }
          }
          setItems(await Promise.all((po.lines ?? []).map(async (line) => {
            const row = mapPoLineToEditableRow(line)
            if (line.price_rule_id && line.price_snapshot?.sheet) {
              try {
                const response = await apiFetch(`/api/quote-rules/${encodeURIComponent(line.price_rule_id)}/sheet-info`)
                const body = await response.json() as { ok?: boolean; info?: PoItemRow['sheetInfo'] }
                if (body.ok && body.info?.has_sheet) {
                  row.sheetInfo = body.info
                  row.sheetInputs = Object.fromEntries(body.info.input_names.map((name) => [name, String(line.price_snapshot?.sheet?.inputs?.[name] ?? '')]))
                  for (const name of Object.keys(body.info.mappings ?? {})) row[`sheet__${name}`] = body.info.mappings[name]
                  for (const [name, value] of Object.entries(row.sheetInputs ?? {})) row[`sheet__${name}`] = value
                }
              } catch { /* keep the captured sheet info from the PO snapshot */ }
            }
            return row
          })))
          setPoHeaderCollapsed((po.lines ?? []).length > 0)
          setStatus('Nháp')
          setIsEditMode(true)
          return
        }

        if (preselectedSupplierId) {
          const target = sups.find((s) => s.id === preselectedSupplierId)
          if (target) {
            applySupplierToPo(target)
            return
          }
        }
      } catch (loadError) {
        if (editPoId && !cancelled) setError(loadError instanceof Error ? loadError.message : 'Không tải được PO cần sửa.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void loadMasters()
    return () => {
      cancelled = true
    }
  }, [editPoId, navigate, preselectedSupplierId])

  const [supplierQuotesMap, setSupplierQuotesMap] = useState<Map<string, SupplierQuote>>(new Map())
  const supplierReqSeqRef = useRef(0)

  const quoteRuleSuggestions = useMemo(() => {
    return buildQuoteRuleSuggestions(
      supplierRules,
      supplierQuotesMap,
      quoteType,
    )
  }, [supplierRules, supplierQuotesMap, quoteType])

  const combinedNameSuggestions = useMemo<PoNameSuggestion[]>(() => {
    const list: PoNameSuggestion[] = []
    const quoteNames = new Set(
      foamQuoteItemNames(quoteRuleSuggestions.map((suggestion) => ({
        supplierId: suggestion.rule.supplier_id,
        supplierName: suggestion.quote?.title ?? '',
        itemName: suggestion.name,
        mappings: {},
        inputNames: [],
      }))),
    )
    const addedQuoteNames = new Set<string>()

    // 1. Suggestions from Supplier Quotes
    for (const s of quoteRuleSuggestions) {
      const canonicalName = [...quoteNames].find((name) => quoteRuleNameKey(name) === quoteRuleNameKey(s.name))
      const nameKey = quoteRuleNameKey(canonicalName ?? s.name)
      if (!nameKey || addedQuoteNames.has(nameKey)) continue
      addedQuoteNames.add(nameKey)
      list.push({
        id: `quote-${s.id}`,
        type: 'quote',
        name: s.name,
        primaryText: s.primaryText,
        secondaryText: s.secondaryText,
        searchText: s.searchText,
        rule: s.rule,
        quote: s.quote,
      })
    }

    // When the selected supplier has quote names, keep this picker scoped to
    // those names. Otherwise every catalog variant (often one per price row)
    // leaks into the supplier-name step and makes the picker unusable.
    if (quoteRuleSuggestions.length === 0) for (const m of systemMaterials) {
      if (m.status !== 'active') continue
      const rawText = `${m.name} ${m.code || ''} ${m.uom_default || ''}`
      list.push({
        id: `mat-${m.id}`,
        type: 'material',
        name: m.name,
        primaryText: m.name,
        secondaryText: `[Danh mục vật tư] · ĐVT: ${m.uom_default || 'Pcs'}`,
        searchText: stripDiacritics(rawText.toLowerCase()),
        material: m,
      })
    }

    return list
  }, [quoteRuleSuggestions, systemMaterials])

  function filterNameSuggestion(item: PoNameSuggestion, query: string): boolean {
    if (!query) return true
    const cleanQ = stripDiacritics(query).trim().toLowerCase()
    if (!cleanQ) return true
    const words = cleanQ.split(/\s+/).filter(Boolean)
    return words.every((w) => item.searchText.includes(w))
  }

  const resolvePriceForLine = useCallback(
    (
      row: PoItemRow,
      targetSupplierId?: string,
      targetDeliveryDate?: string,
      targetRuleId?: string,
    ) => {
      const supplierId = targetSupplierId ?? selectedSupplierRef.current?.id
      if (!supplierId || !row.material_id) return
      if (row.priceSource === 'manual') return

      const asOf = targetDeliveryDate ?? deliveryDate ?? new Date().toISOString().slice(0, 10)
      const qtyRaw = String(row.qty ?? '').replace(/,/g, '').trim()
      const qtyNum = qtyRaw !== '' ? parseFloat(qtyRaw) : 0
      if (isNaN(qtyNum) || qtyNum <= 0) {
        coordinatorRef.current?.invalidateRow(row.id)
        setItems((currentItems) => {
          const idx = currentItems.findIndex((it) => it.id === row.id)
          if (idx === -1) return currentItems
          const current = currentItems[idx]
          if (current.priceSource === 'manual') return currentItems
          const updated = { ...current }
          updated.price = null
          updated.lineTotal = 0
          updated.priceSource = 'none'
          updated.quoteStatus = 'INVALID_QTY'
          updated.priceWarning = 'Số lượng không hợp lệ (> 0)'
          const copy = [...currentItems]
          copy[idx] = updated
          return copy
        })
        return
      }
      const selectedRuleId = targetRuleId ?? row.priceRuleId ?? undefined

      coordinatorRef.current?.requestPrice(
        row.id,
        {
          supplierId,
          materialId: row.material_id,
          spec: row.spec,
          thick: row.t !== undefined && row.t !== '' ? Number(row.t) : undefined,
          width: row.w !== undefined && row.w !== '' ? Number(row.w) : undefined,
          length: row.l !== undefined && row.l !== '' ? Number(row.l) : undefined,
          quality: row.grade || undefined,
          orderUom: row.uom,
          qty: qtyNum,
          asOf,
          selectedRuleId,
        },
        {
          supplierId,
          materialId: row.material_id,
          spec: row.spec,
          thick: row.t !== undefined && row.t !== '' ? Number(row.t) : undefined,
          width: row.w !== undefined && row.w !== '' ? Number(row.w) : undefined,
          length: row.l !== undefined && row.l !== '' ? Number(row.l) : undefined,
          quality: row.grade || undefined,
          density: row.density || row.dinh_luong || undefined,
          uom: row.uom,
          orderUom: row.uom,
          qty: qtyNum,
          asOf,
          selectedRuleId,
          allowEstimate: false,
        },
      )
    },
    [deliveryDate],
  )

  const [rulePickerIndex, setRulePickerIndex] = useState<number | null>(null)
  /** Tính giá dòng bảng tính bằng số nhập (rỗng nếu dòng không có ô nhập); ghi vào đúng dòng đang gắn ruleId. */
  async function calcSheetLine(index: number, ruleId: string, inputs: Record<string, number>) {
    const res = await apiFetch(`/api/quote-rules/${encodeURIComponent(ruleId)}/sheet-calc`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inputs }),
    })
    const body = (await res.json()) as { ok?: boolean; snapshot?: { purchase_price: number }; error?: { message?: string } }
    setItems((prev) => {
      const copy = [...prev]
      const cur = copy[index]
      if (!cur || cur.priceRuleId !== ruleId) return prev
      copy[index] = res.ok && body.ok && body.snapshot
        ? { ...cur, sheetPreview: { purchase_price: body.snapshot.purchase_price }, sheetError: null, lineTotal: cur.isNegotiated ? cur.lineTotal : sheetLineTotalOf(cur.qty, body.snapshot.purchase_price), priceWarning: null, quoteStatus: 'SHEET' }
        : { ...cur, sheetPreview: null, sheetError: body.error?.message ?? 'Không tính được giá từ số nhập' }
      return copy
    })
  }

  function handleSelectCandidateRule(index: number, ruleId: string) {
    const item = items[index]
    const supplierId = selectedSupplierRef.current?.id
    if (!item || !supplierId) return

    // Immediately invalidate current price for this row
    coordinatorRef.current?.invalidateRow(item.id)

    const updatedRow: PoItemRow = {
      ...item,
      price: null,
      lineTotal: 0,
      priceSource: 'none',
      priceRuleId: ruleId,
      quoteStatus: 'RESOLVING',
      priceWarning: 'Đang xác thực giá từ máy chủ...',
      sheetInfo: null,
      sheetPreview: null,
      sheetError: null,
      sheetInputs: { ...(item.sheetInputs ?? {}) },
      sheetChoices: choicesFromRule(
        (sheetRulesByKey[supplierId + '|' + (item.material_id ?? '')] ?? []).find((r) => r.id === ruleId) ?? { id: ruleId },
        poSheetColumns.map((c) => c.name),
      ),
    }

    setItems((prev) => {
      const copy = [...prev]
      copy[index] = updatedRow
      return copy
    })

    // Biết dòng là bảng tính hay không rồi mới lấy giá: dòng bảng tính chờ bấm Tính; dòng cũ lấy giá theo đúng dòng đã chọn.
    void loadSheetInfo(index, ruleId).then((info) => {
      if (info?.has_sheet) {
        const savedInputs = { ...(item.sheetInputs ?? {}) }
        setItems((prev) => {
          const copy = [...prev]
          const cur = copy[index]
          if (cur && cur.priceRuleId === ruleId) {
            copy[index] = { ...cur, quoteStatus: 'SHEET_PENDING', priceWarning: info.input_names.length ? sheetPendingWarning(info.input_names) : null }
          }
          return copy
        })
        const numericInputs = Object.fromEntries(info.input_names.flatMap((name) => {
          const value = Number(String(savedInputs[name] ?? '').replace(/,/g, '.'))
          return Number.isFinite(value) && String(savedInputs[name] ?? '').trim() !== '' ? [[name, value]] : []
        }))
        if (info.input_names.length === 0 || Object.keys(numericInputs).length === info.input_names.length) {
          void calcSheetLine(index, ruleId, numericInputs)
        }
        return
      }
      resolvePriceForLine(updatedRow, supplierId, deliveryDate, ruleId)
    })
  }

  /** Lấy thông tin bảng tính của dòng giá (ô nhập lúc mua, ánh xạ). Trả về null nếu không có bảng tính hoặc lỗi. */
  async function loadSheetInfo(index: number, ruleId: string): Promise<PoItemRow['sheetInfo'] | null> {
    try {
      const res = await apiFetch(`/api/quote-rules/${encodeURIComponent(ruleId)}/sheet-info`)
      const body = (await res.json()) as { ok?: boolean; info?: PoItemRow['sheetInfo'] }
      const info = body.info ?? null
      setItems((prev) => {
        const copy = [...prev]
        const cur = copy[index]
        if (cur && cur.priceRuleId === ruleId) {
          copy[index] = { ...cur, sheetInfo: info, sheetInputs: { ...(cur.sheetInputs ?? {}) }, sheetPreview: null, sheetError: null }
        }
        return copy
      })
      return info
    } catch {
      return null
    }
  }

  /** NCC có cột quy cách: gợi ý từ báo giá không được điền đơn giá hay gắn dòng giá; dòng chỉ gắn khi chọn quy cách. */
  function sheetSafePatch(patch: Partial<PoItemRow>): Partial<PoItemRow> {
    if (!selectedSupplier || poSheetColumns.length === 0) return patch
    const out: Partial<PoItemRow> = { ...patch, price: null, priceSource: 'none', priceRuleId: null, quoteId: null, priceWarning: 'Chưa chọn quy cách' }
    return out
  }

  /** Đổi tên hàng: bỏ các lựa chọn quy cách / số nhập / dòng giá của hàng cũ để người dùng chọn lại theo hàng mới. */
  function resetSheetOnNameChange(item: PoItemRow, newName: string): Partial<PoItemRow> {
    const hasSheetState = Boolean(item.priceRuleId) || Object.keys(item.sheetChoices ?? {}).length > 0 || Object.keys(item.sheetInputs ?? {}).length > 0
    if (!hasSheetState || (item.name ?? '').trim() === newName.trim()) return {}
    return {
      sheetChoices: {},
      sheetInputs: {},
      sheetInfo: null,
      sheetPreview: null,
      sheetError: null,
      priceRuleId: null,
      quoteId: null,
      quoteStatus: undefined,
      price: null,
      lineTotal: 0,
      priceSource: 'none',
      priceWarning: selectedSupplier && poSheetColumns.length > 0 ? 'Chưa chọn quy cách' : null,
    }
  }

  /** Đổi một ô quy cách (lọc dây chuyền): bỏ các lựa chọn không còn khớp dòng nào; dòng đã gắn nhưng không còn khớp thì bỏ gắn. */
  function setSheetChoice(index: number, name: string, value: string, rules: ActiveRuleOption[]) {
    setItems((prev) => {
      const copy = [...prev]
      const cur = copy[index]
      if (!cur) return prev
      let choices: SheetChoices = { ...(cur.sheetChoices ?? {}) }
      if (value) choices[name] = value
      else delete choices[name]
      choices = pruneLaterChoices(rules, choices, name, poSheetColumns.map((c) => c.name))
      const stillBound = Boolean(cur.priceRuleId) && sheetMatchingRules(rules, choices).some((r) => r.id === cur.priceRuleId)
      copy[index] = stillBound
        ? { ...cur, sheetChoices: choices }
        : { ...cur, sheetChoices: choices, priceRuleId: null, quoteStatus: undefined, priceWarning: 'Chưa chọn quy cách', sheetInfo: null, sheetInputs: { ...(cur.sheetInputs ?? {}) }, sheetPreview: null, sheetError: null, price: null, lineTotal: 0, priceSource: 'none' }
      return copy
    })
  }

  function updateSheetInput(index: number, name: string, value: string) {
    const row = items[index]
    if (!row) return
    const sheetInputs = { ...(row.sheetInputs ?? {}), [name]: value }
    const missing = (row.sheetInfo?.input_names ?? []).filter((inputName) => (sheetInputs[inputName] ?? '').trim() === '')
    const timer = sheetCalcTimersRef.current.get(row.id)
    if (timer) clearTimeout(timer)
    sheetCalcTimersRef.current.delete(row.id)
    setItems((prev) => {
      const copy = [...prev]
      const currentIndex = copy.findIndex((item) => item.id === row.id)
      const current = copy[currentIndex]
      if (current && current.priceRuleId === row.priceRuleId) {
        copy[currentIndex] = {
          ...current,
          sheetInputs,
          sheetPreview: null,
          sheetError: null,
          lineTotal: 0,
          priceWarning: missing.length ? 'Nhập ' + missing.join(', ') : null,
        }
      }
      return copy
    })
    if (missing.length || !row.priceRuleId || !row.sheetInfo?.has_sheet) return

    const ruleId = row.priceRuleId
    const inputs: Record<string, number> = {}
    for (const inputName of row.sheetInfo.input_names) {
      const parsed = Number(String(sheetInputs[inputName]).replace(/,/g, '.'))
      if (!Number.isFinite(parsed)) return
      inputs[inputName] = parsed
    }
    const inputKey = JSON.stringify(sheetInputs)
    const nextTimer = setTimeout(() => {
      sheetCalcTimersRef.current.delete(row.id)
      void apiFetch(`/api/quote-rules/${encodeURIComponent(ruleId)}/sheet-calc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inputs }),
      }).then(async (res) => {
        const body = (await res.json()) as { ok?: boolean; snapshot?: { purchase_price: number }; error?: { message?: string } }
        setItems((prev) => prev.map((current) => {
          if (current.id !== row.id || current.priceRuleId !== ruleId || JSON.stringify(current.sheetInputs ?? {}) !== inputKey) return current
          return res.ok && body.ok && body.snapshot
            ? { ...current, sheetPreview: { purchase_price: body.snapshot.purchase_price }, sheetError: null, lineTotal: current.isNegotiated ? current.lineTotal : sheetLineTotalOf(current.qty, body.snapshot.purchase_price), priceWarning: null, quoteStatus: 'SHEET' }
            : { ...current, sheetPreview: null, lineTotal: 0, sheetError: body.error?.message ?? 'Không tính được giá từ số nhập' }
        }))
      }).catch((error: unknown) => {
        setItems((prev) => prev.map((current) => current.id === row.id && current.priceRuleId === ruleId && JSON.stringify(current.sheetInputs ?? {}) === inputKey
          ? { ...current, sheetPreview: null, lineTotal: 0, sheetError: error instanceof Error ? error.message : 'Không kết nối được máy chủ tính giá' }
          : current))
      })
    }, 250)
    sheetCalcTimersRef.current.set(row.id, nextTimer)
  }

  function handleDeliveryDateChange(newDate: string) {
    setDeliveryDate(newDate)
    const activeSupplierId = selectedSupplierRef.current?.id

    setItems((prev) =>
      prev.map((item) => {
        if (item.priceSource === 'manual' || !item.material_id) return item
        // Invalidate auto price immediately
        coordinatorRef.current?.invalidateRow(item.id)

        return {
          ...item,
          price: null,
          priceSource: 'none' as const,
          priceRuleId: null,
          quoteId: null,
          priceWarning: 'Đang tính lại giá theo ngày giao...',
          quoteStatus: 'RESOLVING',
          lineTotal: 0,
        }
      }),
    )

    if (activeSupplierId) {
      items.forEach((item) => {
        if (item.material_id && item.priceSource !== 'manual') {
          resolvePriceForLine(item, activeSupplierId, newDate)
        }
      })
    }
  }

  function applySupplierToPo(sup: Supplier) {
    setSelectedSupplier(sup)
    setStandardVatRate(String(supplierVatRateOf(sup.default_vat_rate)))
    setSupplierInputName(sup.name)
    setSupplierAddress(sup.address || '')
    setSupplierContact(sup.contact_person || '')
    setSupplierPhone(sup.phone || '')
    setOrderNotes(sup.note || '')

    // Clear all pending per-row timers
    coordinatorRef.current?.clear()

    // Invalidate all prices immediately when supplier changes, clearing manual prices and previous quotes
    // Requirement 3: Đổi supplier -> PHẢI CLEAR manual price + manual_reason + provisional total!
    setItems((prev) =>
      prev.map((item) => {
        coordinatorRef.current?.invalidateRow(item.id)
        return {
          ...item,
          price: null,
          lineTotal: 0,
          priceSource: 'none',
          manualReason: '',
          priceReason: '',
          isNegotiated: false,
          priceWarning: item.material_id ? 'Đang tải giá NCC mới...' : (item.name ? 'Chưa chọn vật tư trong danh mục hệ thống' : null),
          quoteStatus: item.material_id ? 'RESOLVING' : 'MATERIAL_NOT_SELECTED',
          priceRuleId: null,
          quoteId: null,
          sheetInfo: null,
          sheetInputs: {},
          sheetPreview: null,
          sheetError: null,
          sheetChoices: {},
        }
      }),
    )

    // D5: Reset rules and cancel previous pending requests
    setSupplierRules([])
    setSupplierQuotesMap(new Map())
    const seq = ++supplierReqSeqRef.current

    // D5: Load active quote rules with cancellation and clear user error feedback
    void (async () => {
      try {
        const quotes = await listQuotesApi({ supplier_id: sup.id })
        if (supplierReqSeqRef.current !== seq) return
        const active = quotes.filter((q) => q.status === 'ACTIVE')
        const qMap = new Map<string, SupplierQuote>()
        active.forEach((q) => qMap.set(q.id, q))

        const rules: PriceRule[] = []
        for (const q of active) {
          const r = await listRulesByQuoteApi(q.id)
          rules.push(...r)
        }
        if (supplierReqSeqRef.current !== seq) return
        setSupplierQuotesMap(qMap)
        setSupplierRules(rules)

        // Resolve prices via server for lines with material_id
        const linesToResolve: PoItemRow[] = []
        setItems((prev) =>
          prev.map((item) => {
            const isEmptyRow = !item.name && !item.kind && !item.spec && !item.qty
            const targetRow = isEmptyRow
              ? (autoFillFromQuote(item, rules, qMap) as PoItemRow)
              : item
            if (targetRow.priceSource === 'manual') return targetRow

            if (targetRow.material_id) {
              linesToResolve.push(targetRow)
              return targetRow
            }

            return {
              ...targetRow,
              price: null,
              lineTotal: 0,
              priceSource: 'none',
              priceWarning: targetRow.name ? 'Chưa chọn vật tư trong danh mục hệ thống' : null,
              quoteStatus: 'MATERIAL_NOT_SELECTED',
              priceRuleId: null,
              quoteId: null,
            }
          }),
        )

        linesToResolve.forEach((r) => resolvePriceForLine(r, sup.id))
      } catch (err: any) {
        if (supplierReqSeqRef.current !== seq) return
        setSupplierRules([])
        setSupplierQuotesMap(new Map())
        setItems((prev) =>
          prev.map((item) => {
            if (item.priceSource === 'manual') return item
            return {
              ...item,
              price: null,
              lineTotal: 0,
              priceSource: 'none',
              priceWarning: 'Không tải được báo giá',
              quoteStatus: 'NO_QUOTE',
              priceRuleId: null,
              quoteId: null,
            }
          }),
        )
        setToastMsg(`Không thể tải bảng giá của nhà cung cấp: ${err?.message || 'Lỗi mạng'}`)
      }
    })()
  }

  function handleSupplierInputChange(name: string) {
    setSupplierInputName(name)
    const match = systemSuppliers.find(
      (s) => s.name.trim().toLowerCase() === name.trim().toLowerCase(),
    )
    if (match) {
      applySupplierToPo(match)
    } else {
      setSelectedSupplier(null)
      ++supplierReqSeqRef.current
      coordinatorRef.current?.clear()
      setSupplierRules([])
      setSupplierQuotesMap(new Map())
      setItems((prev) =>
        prev.map((item) => {
          coordinatorRef.current?.invalidateRow(item.id)
          return {
            ...item,
            price: null,
            lineTotal: 0,
            priceSource: 'none',
            manualReason: '',
            priceReason: '',
            isNegotiated: false,
            priceWarning: 'Chưa chọn nhà cung cấp',
            quoteStatus: 'SUPPLIER_NOT_SELECTED',
            priceRuleId: null,
            quoteId: null,
          }
        }),
      )
    }
  }

  // Row update handlers
  function updateItem(index: number, partial: Partial<PoItemRow>) {
    let rowToResolve: PoItemRow | null = null

    setItems((prev) => {
      const copy = [...prev]
      const prevItem = copy[index]
      let cur = { ...prevItem, ...partial }

      const isTextChange =
        partial.name !== undefined ||
        partial.kind !== undefined ||
        partial.spec !== undefined

      // Kiểm tra xem material_id có bị đổi hoặc bị xóa không
      const materialIdChanged =
        (partial.material_id !== undefined && partial.material_id !== prevItem.material_id) ||
        (isTextChange && partial.material_id === undefined && (
          (partial.name !== undefined && partial.name !== prevItem.name) ||
          (partial.kind !== undefined && partial.kind !== prevItem.kind)
        ))

      if (materialIdChanged) {
        if (partial.material_id === undefined) {
          cur.material_id = undefined
        }
        cur.priceRuleId = null
        cur.quoteId = null
        cur.quoteTitle = null
        cur.ruleItemName = null
        cur.quoteStatus = undefined
        cur.priceReason = null
        cur.candidates = undefined
        // Đổi hoặc xóa vật tư -> reset luôn cả giá nhập tay
        cur.price = null
        cur.priceSource = 'none'
        cur.priceWarning = cur.name ? 'Chưa chọn vật tư trong danh mục hệ thống' : null
        cur.lineTotal = 0
      }

      // Đổi số lượng không xóa dòng báo giá đã chọn; chỉ đổi vật tư, quy cách hoặc đơn vị mới xóa lựa chọn
      const affectsPricing =
        materialIdChanged ||
        partial.spec !== undefined ||
        partial.t !== undefined ||
        partial.w !== undefined ||
        partial.l !== undefined ||
        partial.grade !== undefined ||
        partial.density !== undefined ||
        (partial as any).dinh_luong !== undefined ||
        partial.uom !== undefined

      // Invalidate auto price immediately whenever pricing input changes
      if (affectsPricing && cur.priceSource !== 'manual' && partial.price === undefined) {
        cur.price = null
        cur.priceSource = 'none'
        cur.priceRuleId = null
        cur.quoteId = null
        cur.quoteTitle = null
        cur.ruleItemName = null
        cur.quoteStatus = cur.material_id ? 'RESOLVING' : undefined
        cur.priceReason = null
        cur.priceWarning = !selectedSupplierRef.current
          ? 'Chưa chọn nhà cung cấp'
          : cur.material_id
          ? 'Đang cập nhật giá...'
          : cur.name
            ? 'Chưa chọn vật tư trong danh mục hệ thống'
            : null
        cur.lineTotal = 0
        cur.candidates = undefined
      }

      // If material is missing, manual price, or pricing input changed, invalidate in-flight request immediately
      if (!cur.material_id || cur.priceSource === 'manual' || affectsPricing) {
        coordinatorRef.current?.invalidateRow(cur.id)
      }

      // Đồng bộ name cho dữ liệu nhập qua cột kind còn lưu trên dòng.
      if (partial.kind !== undefined && !partial.name) {
        cur.name = partial.kind
      }

      // Re-apply autoFillFromQuote cho các field phụ thuộc CÒN TRỐNG, bỏ qua các field vừa được sửa
      if (
        partial.price === undefined &&
        (partial.name !== undefined ||
          partial.kind !== undefined ||
          partial.spec !== undefined ||
          partial.density !== undefined ||
          partial.grade !== undefined ||
          partial.t !== undefined ||
          partial.w !== undefined ||
          partial.l !== undefined)
      ) {
        const touchedKeys = Object.keys(partial)
        if (partial.name !== undefined) touchedKeys.push('kind')
        if (partial.kind !== undefined) touchedKeys.push('name')
        cur = autoFillFromQuote(
          cur,
          supplierRules,
          supplierQuotesMap,
          undefined,
          touchedKeys,
        ) as PoItemRow
      }

      // Price source tracking
      if (partial.price !== undefined) {
        cur.priceSource = partial.priceSource || (partial.price != null ? 'manual' : 'none')
        cur.priceWarning = partial.priceWarning !== undefined ? partial.priceWarning : null
        cur.priceRuleId = partial.priceRuleId !== undefined ? partial.priceRuleId : null
        cur.quoteId = partial.quoteId !== undefined ? partial.quoteId : null
      }

      if (cur.priceSource === 'manual') {
        const qtyNum = parseFloat(String(cur.qty || 0).replace(/,/g, '')) || 0
        const priceNum = cur.price != null ? Number(cur.price) / Number(cur.priceBasisQty ?? 1) : 0
        const uomNorm = normalizeUomLabel(cur.uom || '')
        const hasVol = tableColumns.some((c) => c.key === 'vol')
        const hasArea = tableColumns.some((c) => c.key === 'area')

        if ((hasVol || uomNorm === 'm³') && cur.t && cur.w && cur.l) {
          const t = parseFloat(String(cur.t || 0)) || 0
          const w = parseFloat(String(cur.w || 0)) || 0
          const l = parseFloat(String(cur.l || 0)) || 0
          const volumeM3 = (t * w * l * qtyNum) / 1000000000
          cur.lineTotal = Math.round(volumeM3 * priceNum)
        } else if ((hasArea || uomNorm === 'm²') && cur.w && cur.l) {
          const w = parseFloat(String(cur.w || 0)) || 0
          const l = parseFloat(String(cur.l || 0)) || 0
          const areaM2 = (w * l * qtyNum) / 1000000
          cur.lineTotal = Math.round(areaM2 * priceNum)
        } else {
          cur.lineTotal = Math.round(qtyNum * priceNum)
        }
      }

      copy[index] = cur

      // Kích hoạt giải quyết giá nếu dòng có material_id và không phải giá nhập tay
      // Dòng bảng tính: giá do bấm Tính, không lấy theo đường cũ; chưa chọn quy cách thì báo lý do
      if (cur.material_id && cur.priceSource !== 'manual' && selectedSupplier && !cur.sheetInfo?.has_sheet) {
        if (!cur.priceRuleId) {
          cur.priceWarning = 'Chưa chọn quy cách'
          cur.quoteStatus = undefined
        } else {
          rowToResolve = cur
        }
      }
      if (cur.sheetInfo?.has_sheet && cur.sheetPreview && partial.qty !== undefined && !cur.isNegotiated) {
        cur.lineTotal = sheetLineTotalOf(cur.qty, cur.sheetPreview.purchase_price)
      }

      return copy
    })

    if (rowToResolve) {
      resolvePriceForLine(rowToResolve)
    }
  }

  function updateQty(index: number, val: string | number) {
    updateItem(index, { qty: val })
  }

  function updatePrice(index: number, val: string | number) {
    const str = String(val ?? '').trim()
    if (str === '') {
      updateItem(index, { price: null, priceSource: 'none', lineTotal: 0 })
      return
    }
    const p = typeof val === 'number' ? val : parseSheetAmount(str)
    if (p === null || !Number.isFinite(p)) {
      updateItem(index, { price: null, priceSource: 'none', lineTotal: 0 })
      return
    }
    const row = items[index]
    if (row) {
      coordinatorRef.current?.invalidateRow(row.id)
    }
    updateItem(index, {
      price: p,
      priceSource: 'manual',
      quoteStatus: undefined,
      priceWarning: null,
      priceReason: 'Nhập tay',
    })
  }

  function addNewRow() {
    if (isFreePo) {
      setFreeRows((prev) => [
        ...prev,
        {
          id: `row_${newId()}`,
          name: '',
          uom: '',
          qty: '',
          unit_price: '',
          line_total: 0,
          note: '',
        },
      ])
      return
    }
    const newRow = createEmptyRow(quoteType)
    newRow.price = null
    newRow.priceSource = 'none'
    newRow.lineTotal = 0
    setItems((prev) => [...prev, newRow])
    setSelectedRowIdx(items.length)
    setPoHeaderCollapsed(true)
  }

  function deleteRow(index: number) {
    if (items.length <= 1) {
      const resetRow = createEmptyRow(quoteType)
      resetRow.price = null
      resetRow.priceSource = 'none'
      resetRow.lineTotal = 0
      setItems([resetRow])
      showToast('Đã xóa trắng dòng.')
      return
    }
    setItems((prev) => prev.filter((_, i) => i !== index))
    setSelectedRowIdx((prev) => Math.max(0, Math.min(prev, items.length - 2)))
    setActiveMenuRowIdx(null)
    showToast('Đã xóa dòng.')
  }

  function moveRowUp(index: number) {
    if (index === 0) return
    setItems((prev) => {
      const copy = [...prev]
      const temp = copy[index]
      copy[index] = copy[index - 1]
      copy[index - 1] = temp
      return copy
    })
    setSelectedRowIdx(index - 1)
    setActiveMenuRowIdx(null)
  }

  function moveRowDown(index: number) {
    if (index >= items.length - 1) return
    setItems((prev) => {
      const copy = [...prev]
      const temp = copy[index]
      copy[index] = copy[index + 1]
      copy[index + 1] = temp
      return copy
    })
    setSelectedRowIdx(index + 1)
    setActiveMenuRowIdx(null)
  }

  function toggleNegotiatedPrice(index: number) {
    const cur = items[index]
    const nextNeg = !cur.isNegotiated
    let notes = cur.notes || ''
    if (nextNeg) {
      if (!notes.includes('Giá thương lượng')) {
        notes = notes ? `${notes} - Giá thương lượng` : 'Giá thương lượng'
      }
      showToast(`Đã mở khóa giá dòng ${index + 1} (Gắn tem TL)`)
    } else {
      notes = notes.replace(' - Giá thương lượng', '').replace('Giá thương lượng', '').trim()
      showToast(`Đã khóa lại giá dòng ${index + 1}`)
    }
    updateItem(index, { isNegotiated: nextNeg, notes })
    setActiveMenuRowIdx(null)
  }

  // Excel Paste Parsing Engine
  function applyExcelPaste(rawText: string) {
    const lines = rawText.trim().split(/\r?\n/)
    if (!lines.length) return

    // Columns that receive pasted data (skip STT and auto-calc fields)
    const pasteableCols = tableColumns.filter(
      (c) =>
        !['stt', 'id', 'vol', 'area', 'total', 'thanhtien', 'lineTotal'].includes(
          c.key,
        ),
    )

    const newRows: PoItemRow[] = []
    lines.forEach((lineStr, lineIdx) => {
      const rawCells = lineStr.split('\t').map((c) => c.trim())
      if (rawCells.length === 0 || (rawCells.length === 1 && !rawCells[0])) return

      // Strip STT if leading column is a pure row number
      const hasStt = /^\d+$/.test(rawCells[0]) && rawCells.length >= 3
      const cells = hasStt ? rawCells.slice(1) : rawCells

      const row: PoItemRow = createEmptyRow(quoteType)
      row.id = Date.now() + lineIdx + Math.random()

      pasteableCols.forEach((col, cIdx) => {
        if (cIdx >= cells.length) return
        const val = cells[cIdx]
        if (col.key === 'grade') {
          row.grade = val.toUpperCase()
        } else if (col.key === 'uom') {
          row.uom = normalizeUomLabel(val) || val
        } else if (col.key === 'qty' || col.key === 'quantity' || col.key === 'sl') {
          const n = parseFloat(val.replace(/,/g, '')) || 0
          row.qty = n || ''
        } else if (col.key === 'price') {
          row.price = parseFloat(val.replace(/,/g, '')) || 0
        } else if (col.key === 't' || col.key === 'w' || col.key === 'l') {
          const n = parseFloat(val.replace(/,/g, ''))
          row[col.key] = isNaN(n) ? '' : n
        } else {
          row[col.key] = val
        }
      })

      // Calculate lineTotal only if manual price is provided
      const qNum = parseFloat(String(row.qty || 0).replace(/,/g, '')) || 0
      const pNum = parseFloat(String(row.price || 0).replace(/,/g, '')) || 0
      const uomNorm = normalizeUomLabel(row.uom || '')
      const hasVol = tableColumns.some((c) => c.key === 'vol')
      const hasArea = tableColumns.some((c) => c.key === 'area')

      if (row.priceSource === 'manual' && pNum > 0) {
        if ((hasVol || uomNorm === 'm³') && row.t && row.w && row.l) {
          const t = parseFloat(String(row.t || 0)) || 0
          const w = parseFloat(String(row.w || 0)) || 0
          const l = parseFloat(String(row.l || 0)) || 0
          const volM3 = (t * w * l * qNum) / 1000000000
          row.lineTotal = Math.round(volM3 * pNum)
        } else if ((hasArea || uomNorm === 'm²') && row.w && row.l) {
          const w = parseFloat(String(row.w || 0)) || 0
          const l = parseFloat(String(row.l || 0)) || 0
          const areaM2 = (w * l * qNum) / 1000000
          row.lineTotal = Math.round(areaM2 * pNum)
        } else {
          row.lineTotal = Math.round(qNum * pNum)
        }
      } else {
        row.lineTotal = 0
      }

      newRows.push(row)
    })

    if (newRows.length > 0) {
      setItems((prev) => {
        const isOnlyOneEmpty =
          prev.length === 1 && !prev[0].name && !prev[0].qty && !prev[0].spec
        return isOnlyOneEmpty ? newRows : [...prev, ...newRows]
      })
      showToast(`Đã dán thành công ${newRows.length} dòng từ Excel!`)
    }
    setPasteModalOpen(false)
    setPasteText('')
  }

  function handleTablePaste(e: React.ClipboardEvent) {
    if (!isEditMode) return
    const text = e.clipboardData.getData('text/plain')
    if (!text) return
    if (text.includes('\t') || text.includes('\n')) {
      e.preventDefault()
      applyExcelPaste(text)
    }
  }

  // Summary Computations
  const totalQty = useMemo(() => {
    return items.reduce(
      (sum, it) => sum + (parseFloat(String(it.qty || 0).replace(/,/g, '')) || 0),
      0,
    )
  }, [items])

  const subTotal = useMemo(() => {
    return sumPoLineTotals(items)
  }, [items])

  const vatAmount = useMemo(() => {
    return Math.round(subTotal * (parseNumberVi(standardVatRate) ?? 0) / 100)
  }, [standardVatRate, subTotal])

  const grandTotal = useMemo(() => {
    return subTotal + vatAmount
  }, [subTotal, vatAmount])

  const draftPrintModel = useMemo(() => mapPoDraftToPrintModel({
    poNumber, supplier: selectedSupplier, supplierName: supplierInputName, supplierAddress, supplierContact, supplierPhone,
    workshop, deliveryDate, notes: orderNotes, items, vatRate: isFreePo ? parsedFreeVatRate : (parseNumberVi(standardVatRate) ?? 0),
    subtotal: isFreePo ? freeSubTotal : subTotal, vatTotal: isFreePo ? freeVatAmount : vatAmount, grandTotal: isFreePo ? freeGrandTotal : grandTotal,
    isFree: isFreePo, freeColumns, freeRows, signatures: signatures.map((s) => ({ title: s.role, name: s.name })),
  }), [poNumber, selectedSupplier, supplierInputName, supplierAddress, supplierContact, supplierPhone, workshop, deliveryDate, orderNotes, items, isFreePo, parsedFreeVatRate, standardVatRate, freeSubTotal, subTotal, freeVatAmount, vatAmount, freeGrandTotal, grandTotal, freeColumns, freeRows, signatures])

  function togglePoColumn(key: string, visible: boolean) {
    const next = visible ? hiddenPoColumns.filter((item) => item !== key) : [...new Set([...hiddenPoColumns, key])]
    setHiddenPoColumns(next)
    try { localStorage.setItem(poHiddenColumnsKey, JSON.stringify(next)) } catch { /* Nếu trình duyệt chặn lưu, thay đổi vẫn áp dụng trong phiên này. */ }
  }

  function resetPoColumns() {
    setHiddenPoColumns([])
    try { localStorage.removeItem(poHiddenColumnsKey) } catch { /* Không bắt buộc có localStorage. */ }
  }

  // Export Excel
  function exportExcel() {
    const headerRows = [
      ['CÔNG TY TNHH SẢN XUẤT MỘC PHÁT'],
      ['Địa chỉ: Tổ 1 - Ấp Sông Mây - Xã Bắc Sơn - Huyện Trảng Bom - Tỉnh Đồng Nai'],
      ['Tel: +84 613 680 301 | Email: info@mocphatfurniture.com'],
      [],
      ['ĐƠN ĐẶT HÀNG MUA NGUYÊN VẬT LIỆU'],
      [`Số PO: ${poNumber}`, `Ngày: ${formatDateFull(new Date())}`],
      [`Kính gửi: ${supplierInputName}`, `Xưởng nhận hàng: ${workshop}`],
      [`Người liên hệ: ${supplierContact}`, `Điện thoại: ${supplierPhone}`],
      [`Địa chỉ NCC: ${supplierAddress}`],
      [],
    ]

    if (isFreePo) {
      const freeTableHeaders = ['STT', ...freeColumns.map((c) => c.label)]
      const freeDataRows: any[][] = freeRows.map((it, i) => {
        return [
          i + 1,
          ...freeColumns.map((col) => {
            return it[col.key] ?? ''
          }),
        ]
      })
      freeDataRows.push([])
      freeDataRows.push(['', 'TỔNG CỘNG', '', freeTotalQty, '', freeSubTotal])
      freeDataRows.push(['', `THUẾ VAT ${parsedFreeVatRate}%`, '', '', '', freeVatAmount])
      freeDataRows.push(['', 'TỔNG THANH TOÁN', '', '', '', freeGrandTotal])
      freeDataRows.push([])
      freeDataRows.push(['XÁC NHẬN CỦA CÁC BÊN:'])
      freeDataRows.push(signatures.map((s) => s.role.replace('\n', ' - ')))
      freeDataRows.push(signatures.map((s) => s.name))

      const aoa = [...headerRows, freeTableHeaders, ...freeDataRows]
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet(aoa)
      XLSX.utils.book_append_sheet(wb, ws, 'DonHangTuDo')
      XLSX.writeFile(
        wb,
        `DonHang_${poNumber.replace(/[^a-zA-Z0-9]/g, '_')}_2026.xlsx`,
      )
      return
    }

    const tableHeaders = tableColumns.map((c) => c.label)
    const dataRows: any[][] = items.map((it, i) => {
      const qVal = parseFloat(String(it.qty || 0).replace(/,/g, '')) || 0
      return tableColumns.map((col) => {
        if (col.key === 'stt' || col.key === 'id') return i + 1
        if (col.key === 'vol') {
          return (
            (Number(it.t || 0) * Number(it.w || 0) * Number(it.l || 0) * qVal) /
            1000000000
          ).toFixed(4)
        }
        if (col.key === 'area') {
          return (
            (Number(it.w || 0) * Number(it.l || 0) * qVal) /
            1000000
          ).toFixed(4)
        }
        if (col.key === 'total' || col.key === 'thanhtien' || col.key === 'lineTotal') {
          return it.lineTotal
        }
        return it[col.key] ?? ''
      })
    })

    dataRows.push([])
    dataRows.push(['', '', '', '', 'TỔNG CỘNG', totalQty, subTotal, ''])
    dataRows.push(['', '', '', '', `THUẾ VAT ${parseNumberVi(standardVatRate) ?? 0}%`, '', vatAmount, ''])
    dataRows.push(['', '', '', '', 'TỔNG THANH TOÁN', '', grandTotal, ''])
    dataRows.push([])
    dataRows.push(['XÁC NHẬN CỦA CÁC BÊN:'])
    dataRows.push(signatures.map((s) => s.role.replace('\n', ' - ')))
    dataRows.push(signatures.map((s) => s.name))

    const aoa = [...headerRows, tableHeaders, ...dataRows]
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    XLSX.utils.book_append_sheet(wb, ws, 'DonHang')
    XLSX.writeFile(
      wb,
      `DonHang_${poNumber.replace(/[^a-zA-Z0-9]/g, '_')}_2026.xlsx`,
    )
    showToast(`Đã xuất file Excel DonHang_${poNumber}.xlsx`)
  }

  // Save to system API
  async function handleSavePo() {
    setError(null)
    setSubmitting(true)
    try {
      // 1. Resolve or auto-create supplier
      let targetSupplier = selectedSupplier
      if (!targetSupplier && supplierInputName.trim()) {
        const match = systemSuppliers.find(
          (s) =>
            s.name.trim().toLowerCase() ===
            supplierInputName.trim().toLowerCase(),
        )
        if (match) {
          targetSupplier = match
          setSelectedSupplier(match)
        } else {
          try {
            const newSup = await supplierService.create({
              code: '', // server cấp mã khi created_from = 'PO'
              name: supplierInputName.trim(),
              category_ids: [],
              created_from: 'PO',
              default_vat_rate: 8,
              payment_term_id: 'pt-net30',
              lead_time_days: 7,
              status: 'ACTIVE',
              address: supplierAddress.trim() || undefined,
              contact_person: supplierContact.trim() || undefined,
              phone: supplierPhone.trim() || undefined,
              note: orderNotes.trim() || undefined,
            })
            targetSupplier = newSup
            setSystemSuppliers((prev) => [...prev, newSup])
            setSelectedSupplier(newSup)
          } catch (e: any) {
            setError(
              `Lỗi tạo Nhà cung cấp: ${e?.message || 'Không thể tạo NCC'}`,
            )
            setSubmitting(false)
            return
          }
        }
      }

      if (!targetSupplier?.id) {
        setError('Vui lòng nhập hoặc chọn Nhà cung cấp.')
        setSubmitting(false)
        return
      }

      if (isFreePo) {
        if (!deliveryDate.trim()) {
          setError('Vui lòng chọn ngày giao hàng.')
          setSubmitting(false)
          return
        }

        const nonEmptyRows = freeRows.filter((r) => {
          const hasName = typeof r.name === 'string' && r.name.trim() !== ''
          const hasQty = r.qty !== '' && r.qty !== null && r.qty !== undefined
          return hasName || hasQty
        })

        if (nonEmptyRows.length === 0) {
          setError('PO phải có ít nhất một dòng hàng với đầy đủ tên hàng, ĐVT và số lượng (> 0)')
          setSubmitting(false)
          return
        }

        for (let i = 0; i < nonEmptyRows.length; i++) {
          const r = nonEmptyRows[i]
          const name = typeof r.name === 'string' ? r.name.trim() : ''
          const uom = typeof r.uom === 'string' ? r.uom.trim() : ''
          const q = parseNumberVi(r.qty)

          if (!name) {
            setError(`Dòng ${i + 1}: Tên hàng không được để trống`)
            setSubmitting(false)
            return
          }
          if (!uom) {
            setError(`Dòng ${i + 1}: ĐVT không được để trống`)
            setSubmitting(false)
            return
          }
          if (q === null || q <= 0) {
            setError(`Dòng ${i + 1}: Số lượng phải lớn hơn 0`)
            setSubmitting(false)
            return
          }
        }

        const free_layout: FreeLayoutColumn[] = freeColumns.map((col) => ({
          key: col.key,
          label: col.label,
          core: Boolean(col.core),
          type: col.type,
          width: col.width,
        }))

        const payloadLines = nonEmptyRows.map((r) => {
          const extra: Record<string, unknown> = {}
          for (const col of freeColumns) {
            if (!col.core) {
              extra[col.key] = r[col.key] ?? null
            }
          }
          const unitPriceNum =
            r.unit_price != null && r.unit_price !== ''
              ? typeof r.unit_price === 'number'
                ? r.unit_price
                : parseNumberVi(r.unit_price)
              : null
          const qtyNum = typeof r.qty === 'number' ? r.qty : parseNumberVi(r.qty)!
          return {
            id: r.id.startsWith('row_') ? undefined : r.id,
            // Tên gộp với các cột người dùng thêm (cùng quy tắc PO tự do mới tạo).
            name: freeLineDetailName(String(r.name ?? ''), extra, freeColumns),
            uom: String(r.uom ?? '').trim(),
            qty: qtyNum,
            unit_price: unitPriceNum != null && unitPriceNum >= 0 ? unitPriceNum : null,
            note: r.note ? String(r.note).trim() : null,
            extra,
          }
        })

        if (isPoEditMode && editPoId) {
          await patchPurchaseOrder(editPoId, {
            delivery_date: deliveryDate || null,
            delivery_site: workshop || null,
            note: orderNotes || null,
            reason: 'Sửa PO tự do từ lưới tạo đơn hàng',
          })
          await updateFreePoContent(editPoId, {
            vat_rate: parsedFreeVatRate,
            free_layout,
            lines: payloadLines,
            signers: signatures.map((s) => ({ title: s.role, name: s.name })),
          })
          showToast('Đã lưu thay đổi PO tự do.')
          navigate(`/purchasing/po/${encodeURIComponent(editPoId)}`)
          return
        }

        const created = await createFreePo({
          kind: 'FREE',
          supplier_id: targetSupplier.id,
          delivery_date: deliveryDate,
          delivery_site: workshop.trim() || null,
          shipping_address: supplierAddress.trim() || null,
          note: orderNotes.trim() || null,
          vat_rate: parsedFreeVatRate,
          free_layout,
          lines: payloadLines,
          signers: signatures.map((s) => ({ title: s.role, name: s.name })),
        })

        showToast(`Đã tạo thành công PO tự do ${created.code || created.id}`)
        await persistNewPoSignersDefault()
        navigate(`/purchasing/po/${encodeURIComponent(created.id)}`)
        return
      }

      // 2. Validate items
      const validItems = items.filter((it) => it.name || it.spec || it.qty)
      if (validItems.length === 0) {
        setError('Đơn hàng cần có ít nhất 1 dòng vật tư.')
        setSubmitting(false)
        return
      }

      // D6: Block save if price <= 0 or qty <= 0
      for (let i = 0; i < validItems.length; i++) {
        const it = validItems[i]
        const qNum = Number(it.qty)
        const originalLine = isPoEditMode ? (editPoOriginal?.lines ?? []).find((line) => line.id === String(it.id)) : undefined
        const remainsUnpriced = originalLine?.price_source === 'UNPRICED' && it.price == null && !it.priceRuleId
        if (isNaN(qNum) || qNum <= 0) {
          setError(
            `Dòng ${i + 1} (${it.name || it.spec || 'Vật tư'}): Số lượng phải lớn hơn 0. Vui lòng nhập số lượng hợp lệ trước khi tạo đơn hàng.`,
          )
          setSubmitting(false)
          return
        }
        // NCC có cột quy cách: dòng có vật tư nhưng chưa gắn dòng giá thì báo chọn quy cách trước mọi kiểm giá khác.
        if (!remainsUnpriced && selectedSupplier && poSheetColumns.length > 0 && it.material_id && it.priceSource !== 'manual' && !it.priceRuleId) {
          setError(sheetSaveBlocker(it, `Dòng ${i + 1} (${it.name || it.spec || 'Vật tư'})`))
          setSubmitting(false)
          return
        }
        // Dòng bảng tính: máy chủ tính giá từ quy cách và số nhập; đơn giá trên màn hình không dùng để lưu.
        const sheetError = sheetSaveBlocker(it, `Dòng ${i + 1} (${it.name || it.spec || 'Vật tư'})`)
        if (it.sheetInfo?.has_sheet && !remainsUnpriced && !it.isNegotiated) {
          if (sheetError) {
            setError(sheetError)
            setSubmitting(false)
            return
          }
        } else if (!remainsUnpriced) {
          const pNum = Number(it.price)
          if (isNaN(pNum) || pNum <= 0) {
            setError(
              `Dòng ${i + 1} (${it.name || it.spec || 'Vật tư'}): Đơn giá phải lớn hơn 0. Vui lòng nhập giá trước khi tạo đơn hàng.`,
            )
            setSubmitting(false)
            return
          }
          if (it.priceSource === 'auto' && !it.priceRuleId) {
            setError(
              `Dòng ${i + 1} (${it.name || it.spec || 'Vật tư'}): Trạng thái giá tự động không hợp lệ (thiếu mã quy tắc giá). Vui lòng chọn lại báo giá hoặc nhập giá tay.`,
            )
            setSubmitting(false)
            return
          }

        }
      }

      // 3. Resolve materials & prepare lines strictly by material_id
      for (let i = 0; i < validItems.length; i++) {
        const it = validItems[i]
        if (!it.material_id) {
          setError(
            `Dòng ${i + 1} (${it.name || it.spec || 'Vật tư'}): Chưa chọn vật tư trong danh mục hệ thống. Vui lòng chọn từ danh mục hoặc bấm '+ Thêm vật tư mới'.`,
          )
          setSubmitting(false)
          return
        }
        const mat = systemMaterials.find((m) => m.id === it.material_id)
        if (!mat) {
          setError(
            `Dòng ${i + 1}: Không tìm thấy vật tư khớp trong danh mục hệ thống.`,
          )
          setSubmitting(false)
          return
        }
      }

      const unbound = validItems.filter((it) => {
        const pending = isPoEditMode && (editPoOriginal?.lines ?? []).some((line) => line.id === String(it.id) && line.price_source === 'UNPRICED') && it.price == null
        return requiresQuoteRuleForPoSave(it, Boolean(selectedSupplier), pending)
      })
      if (unbound.length > 0) {
        setError('Chưa chọn quy cách cho dòng ' + unbound.map((it) => items.indexOf(it) + 1).join(', ') + ' — chọn ô quy cách hoặc bấm Chọn dòng báo giá')
        setSubmitting(false)
        return
      }
      const missingInput = validItems.filter((it) => it.sheetInfo?.has_sheet && it.sheetInfo.input_names.some((n) => (it.sheetInputs?.[n] ?? '') === ''))
      if (missingInput.length > 0) {
        setError('Chưa nhập số lúc mua cho dòng ' + missingInput.map((it) => items.indexOf(it) + 1).join(', ') + ' rồi bấm Tính')
        setSubmitting(false)
        return
      }

      if (isPoEditMode && editPoId && editPoOriginal) {
        const poId = editPoId
        const originalLines = editPoOriginal.lines ?? []
        await patchPurchaseOrder(poId, {
            delivery_date: deliveryDate || null,
            delivery_site: workshop || null,
            note: orderNotes || null,
          vat_rate: parseNumberVi(standardVatRate) ?? 0,
          signers: signatures.map((s) => ({ title: s.role, name: s.name })),
          reason: 'Sửa PO từ lưới tạo đơn hàng',
        })
        const retainedIds = new Set(validItems.filter((item) => typeof item.id === 'string').map((item) => String(item.id)))
        for (const oldLine of originalLines) {
          if (!retainedIds.has(oldLine.id)) await deletePoLine(poId, oldLine.id, 'Xóa dòng trong chế độ sửa PO dạng lưới')
        }
        for (const item of validItems) {
          const isExisting = typeof item.id === 'string' && originalLines.some((line) => line.id === String(item.id))
          const material = systemMaterials.find((candidate) => candidate.id === item.material_id)
          if (!material || !item.material_id) throw new Error(`Không tìm thấy vật tư cho dòng ${item.name || item.spec}`)
          const captured = capturePoItemSpec(item, material)
          const numericInputs = Object.fromEntries(Object.entries(item.sheetInputs ?? {}).flatMap(([name, value]) => {
            const number = Number(String(value).replace(/,/g, '.'))
            return Number.isFinite(number) && String(value).trim() !== '' ? [[name, number]] : []
          }))
          if (isExisting) {
            const oldLine = originalLines.find((line) => line.id === String(item.id))!
            const payload: PatchPoLinePayload = { reason: 'Sửa PO từ lưới tạo đơn hàng' }
            if (Number(item.qty) !== Number(oldLine.order_qty)) payload.order_qty = Number(item.qty)
            if (item.uom !== oldLine.order_uom) payload.order_uom = item.uom
            if ((item.spec || '') !== (oldLine.spec_text ?? oldLine.spec ?? '')) payload.spec_text = item.spec || null
            if ((item.material_id || '') !== oldLine.material_id) payload.material_id = item.material_id
            if (item.priceRuleId && item.priceRuleId !== oldLine.price_rule_id) payload.selected_rule_id = item.priceRuleId
            if (item.sheetInfo?.has_sheet && item.priceRuleId) {
              payload.selected_rule_id = item.priceRuleId
              payload.sheet_inputs = numericInputs
            }
            if (item.priceSource === 'manual' && Number(item.price) > 0 && (oldLine.price_source !== 'MANUAL' || Number(item.price) !== Number(oldLine.unit_price_actual))) {
              payload.manual_price = Number(item.price)
              payload.manual_reason = item.manualReason?.trim() || (oldLine.price_source === 'UNPRICED' ? 'Nhập giá trên PO (từ BOM chưa có báo giá)' : 'Nhập tay theo thỏa thuận')
              payload.price_basis_qty = Number(item.priceBasisQty ?? 1)
              payload.price_uom = item.priceUom || item.uom
            }
            if (item.priceSource === 'manual' && item.manualReason !== oldLine.manual_reason) {
              payload.manual_reason = item.manualReason?.trim() || 'Nhập tay theo thỏa thuận'
            }
            if (item.priceSource === 'manual' && Number(item.priceBasisQty ?? 1) !== Number(oldLine.price_basis_qty ?? 1)) payload.price_basis_qty = Number(item.priceBasisQty ?? 1)
            if (item.priceSource === 'manual' && (item.priceUom || item.uom) !== (oldLine.price_uom || oldLine.order_uom)) payload.price_uom = item.priceUom || item.uom
            if (Object.keys(payload).length > 1) await patchPoLine(poId, String(item.id), payload)
          } else {
            const pending = item.priceSource !== 'manual' && !item.priceRuleId && !(Number(item.price) > 0)
            const payload: CreatePoLinePayload = {
              material_id: material.id,
              spec_text: captured.spec,
              spec_attributes: item.sheetInfo?.has_sheet ? sheetLineSpecAttributes(item.sheetInfo.mappings) : captured.spec_attributes,
              order_qty: Number(item.qty),
              order_uom: item.uom || 'Pcs',
              price_basis_qty: item.priceSource === 'manual' ? Number(item.priceBasisQty ?? 1) : undefined,
              price_uom: item.priceSource === 'manual' ? item.priceUom || item.uom : undefined,
              selected_rule_id: item.priceRuleId || undefined,
              sheet_inputs: item.sheetInfo?.has_sheet ? numericInputs : undefined,
              manual_price: item.priceSource === 'manual' && Number(item.price) > 0 ? Number(item.price) : undefined,
              manual_reason: item.priceSource === 'manual' ? item.manualReason?.trim() || 'Nhập tay theo thỏa thuận' : undefined,
              price_pending: pending,
              requirement_line_id: undefined,
              reason: item.notes || undefined,
            }
            await createPoLine(poId, payload)
          }
        }
        showToast(`Đã lưu thay đổi ${editPoOriginal.code}.`)
        navigate(`/purchasing/po/${encodeURIComponent(poId)}`)
        return
      }

      const linePayloads: CreatePoLinePayload[] = []
      for (const it of validItems) {
        const mat = systemMaterials.find((m) => m.id === it.material_id)!
        const isManual = it.priceSource === 'manual' || it.isNegotiated || !it.priceRuleId
        if (isManual && (!Number.isFinite(Number(it.priceBasisQty ?? 1)) || Number(it.priceBasisQty ?? 1) <= 0)) throw new Error('Giá cho N đơn vị phải lớn hơn 0')
        const manualReason = isManual
          ? (it.manualReason || (it.isNegotiated ? 'Giá thương lượng' : 'Nhập tay theo thỏa thuận'))
          : null

        const captured = capturePoItemSpec(it, mat)
        linePayloads.push({
          material_id: mat.id,
          spec_text: captured.spec,
          spec_attributes: it.sheetInfo?.has_sheet ? sheetLineSpecAttributes(it.sheetInfo.mappings) : captured.spec_attributes,
          price_basis_qty: isManual ? Number(it.priceBasisQty ?? 1) : undefined,
          price_uom: isManual ? it.priceUom || undefined : undefined,
          order_qty: Number(it.qty),
          order_uom: it.uom || 'Tấm',
          thick: typeof it.t === 'number' ? it.t : undefined,
          width: typeof it.w === 'number' ? it.w : undefined,
          length: typeof it.l === 'number' ? it.l : undefined,
          manual_price: isManual ? Number(it.price) : undefined,
          manual_reason: manualReason,
          selected_rule_id: it.priceRuleId || undefined,
          sheet_inputs: it.sheetInfo?.has_sheet
            ? Object.fromEntries(it.sheetInfo.input_names.map((n) => [n, Number(String(it.sheetInputs?.[n] ?? '').replace(/,/g, '.'))]))
            : undefined,
          quality: it.grade || undefined,
          reason: it.notes || undefined,
        })
      }

      // 4. Map status: 'Đã Gửi NCC' -> 'SENT' (có hiệu lực), 'Nháp' -> 'DRAFT', etc.
      const poStatusMap: Record<
        string,
        'DRAFT' | 'SENT' | 'PARTIAL' | 'RECEIVED' | 'CANCELLED'
      > = {
        'Nháp': 'DRAFT',
        'Đã Gửi NCC': 'SENT',
        'Nhận 1 Phần': 'PARTIAL',
        'Đã Nhận Hàng': 'RECEIVED',
        'Hủy Đơn Hàng': 'CANCELLED',
      }
      const targetStatus = poStatusMap[status] || 'SENT'

      const payload: CreatePoPayload = {
        code: poNumber.trim() || undefined,
        supplier_id: targetSupplier.id,
        status: targetStatus,
        delivery_date: deliveryDate,
        delivery_site: workshop,
        note: orderNotes,
        lines: linePayloads,
        vat_rate: parseNumberVi(standardVatRate) ?? 0,
        signers: signatures.map((s) => ({ title: s.role, name: s.name })),
      }

      const res = await createPurchaseOrder(payload)
      await persistNewPoSignersDefault()
      showToast(
        `Đã tạo đơn hàng ${res.code} thành công (${status})! Đang chuyển đến trang tổng thể...`,
      )
      setTimeout(() => {
        navigate('/purchasing/po')
      }, 700)
    } catch (err: any) {
      setError(err?.message || 'Lỗi khi lưu đơn hàng vào hệ thống.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-slate-500">
        Đang tải biểu mẫu đơn hàng...
      </div>
    )
  }

  return (
    <div className={`flex h-full min-h-0 flex-col gap-2 overflow-hidden${isPoEditMode ? ' po-edit-workspace' : ''}`}>
      {/* Embedded CSS for Excel-like All Borders & No Spin Buttons */}
      <style>{`
        .po-excel-grid {
          width: 100%;
          table-layout: fixed;
          border-collapse: collapse;
          border: 1.5px solid #64748b;
        }
        .po-excel-grid th {
          border: 1px solid #94a3b8;
          background-color: #f1f5f9;
          color: #0f172a;
          font-weight: 700;
          font-size: 11px;
          line-height: 1.25;
          padding: 6px 3px;
          text-align: center;
          vertical-align: middle;
          word-break: break-word;
          white-space: normal;
        }
        .po-excel-grid td {
          border: 1px solid #94a3b8;
          padding: 0;
          vertical-align: middle;
          font-size: 12px;
          word-break: break-word;
          white-space: normal;
          background-color: #ffffff;
          height: 34px;
          max-height: 34px;
          overflow: hidden;
        }
        .po-excel-grid tbody tr:hover td {
          background-color: #f8fafc;
        }
        .po-excel-grid tbody tr.row-selected td {
          background-color: #eff6ff;
        }
        .po-excel-grid .cell-input {
          width: 100%;
          height: 100%;
          min-height: 28px;
          border: none;
          outline: none;
          background: transparent;
          padding: 3px 5px;
          font-size: 12px;
          font-family: inherit;
          box-sizing: border-box;
        }
        .po-excel-grid .cell-input:focus {
          background-color: #dbeafe;
          box-shadow: inset 0 0 0 2px #2563eb;
        }
        .po-excel-grid .cell-view {
          width: 100%;
          padding: 3px 5px;
          font-size: 12px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .po-excel-grid .cell-calc {
          background-color: #f8fafc;
          color: #0f172a;
          font-weight: 700;
          text-align: right;
          padding: 3px 6px;
          /* Ngoại lệ hợp lệ: Căn thẳng hàng các chữ số trong cột tính toán tự động */
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        }
        .po-excel-grid input[type=number]::-webkit-inner-spin-button,
        .po-excel-grid input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .po-excel-grid input[type=number] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
        @media screen {
          .print-only { display: none !important; }
          .po-edit-workspace .print-brand-header { display: none; }
          .po-edit-workspace .print-sheet { padding-top: 1rem; }
        }
        @media print {
          .print-sheet { display: none !important; }
          .po-draft-print-only { display: block !important; }
          @page po-landscape { size: A4 landscape; margin: 10mm; }
          .po-print-root { page: po-landscape; }
          .po-print-root {
            width: 100% !important; max-width: none !important; margin: 0 !important; padding: 0 !important;
            border: 0 !important; border-radius: 0 !important; box-shadow: none !important;
            overflow: visible !important; font-family: Inter, sans-serif !important;
          }
          .po-print-root input, .po-print-root textarea {
            border: 0 !important; outline: 0 !important; box-shadow: none !important;
            background: transparent !important; padding: 0 !important; color: inherit !important; appearance: none !important;
          }
          .po-print-root input::placeholder, .po-print-root textarea::placeholder { color: transparent !important; }
          .po-print-root button { display: none !important; }
          .po-print-root .po-print-surface, .po-print-root .po-print-notes { background: transparent !important; border-color: transparent !important; }
          .po-print-root .po-print-grid, .po-print-root .po-excel-grid, .po-print-root .free-sheet-table-container table {
            width: 100% !important; min-width: 0 !important; table-layout: fixed !important; border-collapse: collapse !important; font-size: 8pt !important;
          }
          .po-print-root .po-print-grid th, .po-print-root .po-print-grid td,
          .po-print-root .po-excel-grid th, .po-print-root .po-excel-grid td,
          .po-print-root .free-sheet-table-container th, .po-print-root .free-sheet-table-container td {
            height: auto !important; max-height: none !important; min-height: 0 !important; overflow: visible !important;
            white-space: normal !important; overflow-wrap: anywhere !important; word-break: break-word !important; text-overflow: clip !important;
          }
          .po-print-root .po-excel-grid .cell-view { white-space: normal !important; overflow: visible !important; text-overflow: clip !important; }
          .po-print-root .free-sheet-table-container { border: 0 !important; box-shadow: none !important; overflow: visible !important; }
          .po-print-root .free-sheet-table-container > div { max-height: none !important; overflow: visible !important; }
          .po-print-root .free-sheet-table-container svg { display: none !important; }
          .po-print-root .po-print-signatures { width: 100% !important; }
          .po-print-root .po-delivery-date { border: 0 !important; background: transparent !important; }
          .po-print-root .po-print-notes,
          .po-print-root .po-print-signatures { break-inside: avoid; page-break-inside: avoid; }
          .po-print-root .po-print-signatures .signature-line { margin-top: 0.5rem !important; }
          .po-print-root .po-print-notes .h-16 { height: auto !important; min-height: 0 !important; }
          .po-print-root .po-delivery-date,
          .po-print-root .po-delivery-date input { border-color: transparent !important; box-shadow: none !important; }
          .po-print-root .free-sheet-table-container thead { display: table-header-group; }
          .po-print-root .free-sheet-table-container tr { break-inside: avoid; page-break-inside: avoid; }
          .po-print-root .free-sheet-table-container th,
          .po-print-root .free-sheet-table-container td { white-space: normal !important; overflow-wrap: anywhere !important; word-break: break-word !important; }
          .po-edit-workspace .print-brand-header { display: grid !important; }
          .po-print-root .print-only { display: table !important; }
          .po-print-root .po-free-print-table { margin-top: 1.25rem; }
          .po-print-root .po-free-print-table th,
          .po-print-root .po-free-print-table td { border: 1px solid #1f2937 !important; padding: 5px 6px !important; }
          .po-print-root .po-free-print-table th { background: #f1f5f9 !important; font-weight: 700 !important; text-align: center; }
          .po-print-root .po-free-print-table tfoot th { background: #f1f5f9 !important; }
          .po-print-root .po-free-print-table tfoot tr:last-child th { font-weight: 900 !important; }
          .po-print-root .po-free-print-table { break-inside: auto; page-break-inside: auto; }
        }
      `}</style>

      <div className="no-print">
        <Breadcrumb
          items={[
            { label: 'Trang chủ', to: '/' },
            { label: 'Mua hàng', to: '/purchasing' },
            { label: 'PO', to: '/purchasing/po' },
            { label: isPoEditMode ? `Sửa đơn hàng ${poNumber}` : 'Tạo Đơn Hàng Mới' },
          ]}
        />
      </div>

      {isPoEditMode && <h1 className="sr-only">Sửa đơn hàng {poNumber}</h1>}

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900 no-print flex items-center justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-700 font-bold ml-2"
          >
            ✕
          </button>
        </div>
      ) : null}

      {/* TOP ACTION BAR */}
      <div className="shrink-0 bg-white rounded-xl border border-slate-300 p-3.5 shadow-xs flex flex-wrap justify-between items-center gap-3 no-print">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center text-xs text-slate-500 font-medium">
            <span>Tạo Đơn Hàng PO</span>
          </div>

          {!isPoEditMode && (
            <label className="inline-flex items-center gap-2 cursor-pointer select-none px-2.5 py-1 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition text-xs font-semibold text-slate-700">
              <input
                type="checkbox"
                role="switch"
                aria-checked={isFreePo}
                checked={isFreePo}
                onChange={(e) => {
                  const checked = e.target.checked
                  setIsFreePo(checked)
                  const newParams = new URLSearchParams(searchParams)
                  if (checked) {
                    newParams.set('free', '1')
                  } else {
                    newParams.delete('free')
                  }
                  navigate({ search: newParams.toString() }, { replace: true })
                }}
                className="sr-only"
              />
              <span
                aria-hidden="true"
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                  isFreePo ? 'bg-blue-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    isFreePo ? 'translate-x-4.5' : 'translate-x-0.5'
                  }`}
                />
              </span>
              <span>PO tự do</span>
            </label>
          )}

          {/* Status Dropdown */}
          <div className="relative inline-block text-left status-menu-container">
            <button
              type="button"
              onClick={() => setStatusMenuOpen(!statusMenuOpen)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700 border border-slate-300 transition shadow-2xs"
            >
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  status === 'Hủy Đơn Hàng'
                    ? 'bg-red-500'
                    : status === 'Đã Gửi NCC'
                      ? 'bg-blue-500'
                      : status === 'Đã Nhận Hàng'
                        ? 'bg-emerald-500'
                        : status === 'Nhận 1 Phần'
                          ? 'bg-amber-500'
                          : 'bg-slate-400'
                }`}
              ></span>
              <span
                className={status === 'Hủy Đơn Hàng' ? 'text-red-600 font-bold' : ''}
              >
                {status}
              </span>
              <span className="text-slate-400 text-[10px]">▼</span>
            </button>
            {statusMenuOpen && (
              <div className="absolute left-0 mt-1.5 w-44 rounded-lg shadow-xl bg-white border border-slate-200 ring-1 ring-black ring-opacity-5 z-50 overflow-hidden py-1 text-xs">
                {(
                  [
                    'Nháp',
                    'Đã Gửi NCC',
                    'Nhận 1 Phần',
                    'Đã Nhận Hàng',
                  ] as const
                ).map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => {
                      setStatus(st)
                      setStatusMenuOpen(false)
                      showToast(`Trạng thái: ${st}`)
                    }}
                    className="w-full text-left px-3.5 py-2 text-slate-700 hover:bg-slate-100 flex items-center gap-2 font-medium"
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        st === 'Đã Gửi NCC'
                          ? 'bg-blue-500'
                          : st === 'Đã Nhận Hàng'
                            ? 'bg-emerald-500'
                            : st === 'Nhận 1 Phần'
                              ? 'bg-amber-500'
                              : 'bg-slate-400'
                      }`}
                    ></span>
                    {st}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setStatus('Hủy Đơn Hàng')
                    setStatusMenuOpen(false)
                    showToast('Đã chuyển sang trạng thái: Hủy Đơn Hàng')
                  }}
                  className="w-full text-left px-3.5 py-2 text-red-600 hover:bg-red-50 flex items-center gap-2 font-bold border-t border-slate-100"
                >
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Hủy Đơn Hàng
                </button>
              </div>
            )}
          </div>

          {/* Mode Badge & Format Selector */}
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
              isEditMode
                ? 'bg-amber-100 text-amber-900 border border-amber-300'
                : 'bg-slate-100 text-slate-700 border border-slate-300'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                isEditMode ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'
              }`}
            ></span>
            {isEditMode ? 'Đang chỉnh sửa' : 'Đang khóa xem'}
          </span>

          {isEditMode && !selectedSupplier && (
            <span className="text-xs text-slate-500 ml-2">Chọn nhà cung cấp để hiện cột theo báo giá</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle Lock / Edit Button */}
          <button
            type="button"
            onClick={() => {
              setIsEditMode(!isEditMode)
              showToast(
                isEditMode
                  ? 'Đã khóa xem đơn hàng.'
                  : 'Đã mở chế độ chỉnh sửa đơn hàng.',
              )
            }}
            className={`inline-flex items-center px-3.5 py-1.5 rounded text-xs font-bold transition shadow-2xs ${
              isEditMode
                ? 'bg-slate-700 hover:bg-slate-800 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
            }`}
          >
            {isEditMode ? '🔒 Khóa xem' : '✏️ Chỉnh đơn hàng'}
          </button>

          {isEditMode && (
            <>
              {!isFreePo && (
                <button
                  type="button"
                  onClick={() => setPasteModalOpen(true)}
                  className="inline-flex items-center px-3 py-1.5 bg-slate-100 border border-slate-300 rounded text-xs font-semibold text-slate-700 hover:bg-slate-200 transition shadow-2xs"
                >
                  Paste từ Excel
                </button>
              )}

              <button
                type="button"
                onClick={addNewRow}
                className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded text-xs font-bold text-slate-800 hover:bg-slate-50 transition shadow-2xs"
              >
                + Thêm dòng
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded text-xs font-semibold text-slate-700 hover:bg-slate-50 transition shadow-2xs"
          >
            In đơn hàng
          </button>

          <button
            type="button"
            onClick={exportExcel}
            className="inline-flex items-center px-3.5 py-1.5 bg-emerald-700 rounded text-xs font-bold text-white hover:bg-emerald-800 transition shadow-2xs"
          >
            Xuất EXCEL
          </button>

          {!isFreePo && (
            <div className="relative">
              <button type="button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50" aria-expanded={columnMenuOpen} onClick={() => setColumnMenuOpen((open) => !open)}>Cột · {visibleTableColumns.length}</button>
              {columnMenuOpen && <div className="absolute right-0 z-40 mt-1 max-h-[70vh] w-72 overflow-y-auto rounded border border-slate-200 bg-white p-3 shadow-lg">
                <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-2"><strong className="text-xs">Cột hiển thị trong PO</strong><button type="button" className="text-xs text-blue-700 underline" onClick={resetPoColumns}>Đặt lại</button></div>
                {tableColumns.map((column) => <label key={column.key} className="flex cursor-pointer items-center gap-2 py-1.5 text-xs text-slate-700"><input type="checkbox" checked={!hiddenPoColumns.includes(column.key)} disabled={column.key === 'stt' || column.key === 'id' || column.key.startsWith('sheet__')} onChange={(event) => togglePoColumn(column.key, event.target.checked)}/><span>{column.label}</span>{column.key === 'stt' || column.key === 'id' || column.key.startsWith('sheet__') ? <span className="ml-auto text-[10px] text-slate-400">luôn hiện</span> : null}</label>)}
                <p className="mt-2 border-t border-slate-100 pt-2 text-[10px] text-slate-500">Ẩn cột chỉ đổi chế độ xem; dữ liệu PO vẫn được lưu. Xuất Excel hiện dùng toàn bộ cột mẫu.</p>
              </div>}
            </div>
          )}

          <button
            type="button"
            disabled={submitting}
            onClick={() => void handleSavePo()}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 rounded-lg text-xs font-bold text-white hover:bg-blue-700 transition shadow-2xs disabled:opacity-50 cursor-pointer"
            title={isPoEditMode ? 'Lưu thay đổi PO' : 'Tạo đơn hàng PO và lưu vào hệ thống'}
          >
            {submitting ? (isPoEditMode ? 'Đang lưu thay đổi...' : 'Đang tạo PO...') : (isPoEditMode ? 'Lưu thay đổi PO' : 'Tạo PO (Lưu đơn hàng)')}
          </button>
          {isPoEditMode && <button type="button" disabled={submitting} onClick={() => navigate(`/purchasing/po/${encodeURIComponent(editPoId!)}`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50">Hủy</button>}
        </div>
      </div>

      {/* PRINTABLE DOCUMENT SHEET (A4 LANDSCAPE NO DECORATIVE ICONS) */}
      <div className="po-draft-print-only hidden print:block">
        <PoPrintView po={draftPrintModel.po} supplier={draftPrintModel.supplier} />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
      <div className="bg-white rounded-xl border border-slate-300 shadow-lg p-6 sm:p-8 print-sheet w-full max-w-none mb-4 transition-all">
        {/* 1. Header Công ty & Đơn hàng */}
        <div className="print-brand-header grid grid-cols-2 gap-6 pb-5 border-b-2 border-slate-800">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-white">
                <img
                  src="/logo-mocphat.jpg"
                  alt="Mộc Phát"
                  className="h-full w-full object-contain"
                />
              </div>
              <div>
                <h2 className="text-sm font-black uppercase text-slate-900 tracking-tight">
                  CÔNG TY TNHH SẢN XUẤT MỘC PHÁT
                </h2>
                <p className="text-[11px] text-slate-600">
                  Tổ 1 - Ấp Sông Mây - Xã Bắc Sơn - Huyện Trảng Bom - Tỉnh Đồng Nai
                </p>
              </div>
            </div>
            <div className="text-[11px] text-slate-600 font-mono-num mt-1 space-y-0.5">
              <p>
                <strong>(Tel):</strong> +84 613 680 301 &nbsp;|&nbsp;{' '}
                <strong>(Fax):</strong> +84 613 680 301
              </p>
              <p>
                <strong>Email:</strong> info@mocphatfurniture.com &nbsp;|&nbsp;{' '}
                <strong>Website:</strong> mocphatfurniture.com
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end text-right">
            <h1 className="text-2xl font-black text-slate-900 uppercase tracking-wider mb-2">
              ĐƠN ĐẶT HÀNG
            </h1>
            <div className="text-xs space-y-1 font-mono-num text-slate-700">
              <p className="flex items-center justify-end gap-1">
                <strong>Số PO:</strong>{' '}
                {isEditMode && !isPoEditMode ? (
                  <input
                    type="text"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    className="text-blue-700 font-bold text-sm bg-transparent border-b border-dashed border-slate-300 hover:border-slate-500 focus:border-blue-500 outline-none text-right w-44"
                  />
                ) : (
                  <span className="text-blue-700 font-bold text-sm">{poNumber}</span>
                )}
              </p>
              <p>
                <strong>Ngày:</strong>{' '}
                <span id="printDate">{formatDateFull(new Date())}</span>
              </p>
            </div>
          </div>
        </div>

        {/* 2. Thông tin NCC & Giao hàng */}
        {poHeaderCollapsed ? (
          <div className="mt-5 flex min-h-10 items-center gap-3 border border-slate-200 rounded-lg bg-slate-50 px-4 py-2 text-xs">
            <span className="min-w-0 flex-1 truncate">
              {supplierInputName || 'Chưa chọn nhà cung cấp'} · {workshop || 'Chưa chọn xưởng'} · Giao {deliveryDate ? deliveryDate.split('-').reverse().join('/') : '—'}
            </span>
            <button type="button" className="shrink-0 text-sky-700 hover:text-sky-900" onClick={() => setPoHeaderCollapsed(false)}>✎ Sửa</button>
          </div>
        ) : (
        <div className="mt-5 bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs relative">
          <div className="mb-2 flex justify-end">
            <button type="button" className="text-xs text-sky-700 hover:text-sky-900" onClick={() => setPoHeaderCollapsed(true)}>Thu gọn</button>
          </div>
          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-8 space-y-2.5">
              <div className="flex items-center">
                <span className="w-24 font-bold text-slate-700 shrink-0">Kính gửi:</span>
                <div className="flex-1 relative">
                  {isEditMode && !isPoEditMode ? (
                    <>
                      <input
                        list="supplierDatalistApp"
                        value={supplierInputName}
                        onChange={(e) => handleSupplierInputChange(e.target.value)}
                        className="w-full bg-white border border-slate-300 rounded px-2.5 py-1 text-xs font-bold text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="Gõ tên hoặc chọn Nhà cung cấp..."
                      />
                      <datalist id="supplierDatalistApp">
                        {systemSuppliers.map((s) => (
                          <option key={s.id} value={s.name} />
                        ))}
                      </datalist>
                    </>
                  ) : (
                    <span className="font-bold text-slate-900 text-sm">
                      {supplierInputName || '—'}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <span className="w-24 font-bold text-slate-700 shrink-0">Địa chỉ:</span>
                {isEditMode && !isPoEditMode ? (
                  <input
                    type="text"
                    value={supplierAddress}
                    onChange={(e) => setSupplierAddress(e.target.value)}
                    className="flex-1 bg-transparent border-b border-dashed border-slate-400 outline-none text-slate-800 py-0.5 text-xs focus:border-blue-500"
                    placeholder="Địa chỉ nhà cung cấp..."
                  />
                ) : (
                  <span className="text-slate-800">{supplierAddress || '—'}</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center">
                  <span className="w-24 font-bold text-slate-700 shrink-0">Liên hệ:</span>
                  {isEditMode && !isPoEditMode ? (
                    <input
                      type="text"
                      value={supplierContact}
                      onChange={(e) => setSupplierContact(e.target.value)}
                      className="flex-1 bg-transparent border-b border-dashed border-slate-400 outline-none text-slate-800 py-0.5 text-xs focus:border-blue-500"
                      placeholder="Người liên hệ..."
                    />
                  ) : (
                    <span className="text-slate-800">{supplierContact || '—'}</span>
                  )}
                </div>

                <div className="flex items-center">
                  <span className="w-20 font-bold text-slate-700 shrink-0">Điện thoại:</span>
                  {isEditMode && !isPoEditMode ? (
                    <input
                      type="text"
                      value={supplierPhone}
                      onChange={(e) => setSupplierPhone(e.target.value)}
                      className="flex-1 bg-transparent border-b border-dashed border-slate-400 outline-none text-slate-800 font-mono-num py-0.5 text-xs focus:border-blue-500"
                      placeholder="Số điện thoại..."
                    />
                  ) : (
                    <span className="text-slate-800 font-mono-num">
                      {supplierPhone || '—'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="col-span-4 border-l border-slate-200 pl-4 flex flex-col justify-center space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">
                  XƯỞNG NHẬN HÀNG
                </label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={workshop}
                    onChange={(e) => setWorkshop(e.target.value)}
                    className="w-full bg-amber-50 border border-amber-300 px-3 py-1.5 text-xs font-black text-slate-900 uppercase tracking-wider text-center rounded focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                ) : (
                  <div className="w-full bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-black text-slate-900 uppercase text-center rounded">
                    {workshop || '—'}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1 flex items-center justify-between">
                  <span>Ngày giao hàng</span>
                  <span className="text-[10px] text-blue-600 font-mono">
                    {deliveryDate ? deliveryDate.split('-').reverse().join('/') : ''}
                  </span>
                </label>
                {isEditMode ? (
                  <DateInput
                    value={deliveryDate}
                    onChange={handleDeliveryDateChange}
                    className="w-full text-xs font-mono-num text-slate-800"
                  />
                ) : (
                  <div className="po-delivery-date text-xs font-mono-num text-slate-800 px-2 py-1 bg-white border border-slate-200 rounded text-center">
                    {deliveryDate ? deliveryDate.split('-').reverse().join('/') : '—'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* 3. Bảng Chi Tiết Đơn Hàng */}
        {isFreePo ? (
          <div className="mt-5 w-full space-y-4">
            <div className="no-print">
              <FreeSheetTable
                className="po-free-sheet"
                columns={freeColumns}
                rows={freeRows}
                onChange={({ columns: nextCols, rows: nextRows }) => {
                  setFreeColumns(nextCols)
                  setFreeRows(nextRows)
                }}
                readOnly={!isEditMode}
                totalKey="line_total"
                hideFooter={true}
                computeRow={computeFreeRow}
              />
            </div>

            <table className="po-print-grid po-excel-grid po-free-print-table print-only" data-testid="po-free-print-table">
              <thead>
                <tr>
                  <th style={{ width: `${(48 / freePrintTotalWeight * 100).toFixed(2)}%` }}>STT</th>
                  {freeColumns.map((column, index) => <th key={column.key} style={{ width: `${(freePrintColumnWeights[index] / freePrintTotalWeight * 100).toFixed(2)}%` }}>{column.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {freeRows
                  .map((row, index) => ({ row: computeFreeRow(row), index }))
                  .filter(({ row }) => freeColumns.some((column) => String(row[column.key] ?? '').trim() !== ''))
                  .map(({ row, index }) => (
                    <tr key={row.id}>
                      <td className="text-center">{index + 1}</td>
                      {freeColumns.map((column) => {
                        const value = row[column.key]
                        const display = column.type === 'currency' && typeof value === 'number'
                          ? formatCurrency(value)
                          : column.type === 'number' && typeof value === 'number'
                            ? formatSheetAmount(value)
                            : String(value ?? '')
                        return <td key={column.key} className={column.type === 'number' || column.type === 'currency' ? 'text-right' : ''}>{display}</td>
                      })}
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={freePrintAmountIndex} className="text-left">TỔNG CỘNG TIỀN HÀNG</th>
                  <th className="text-right">{formatCurrency(freeSubTotal)}</th>
                  {freePrintTrailingColumns > 0 && <th colSpan={freePrintTrailingColumns}></th>}
                </tr>
                <tr>
                  <th colSpan={freePrintAmountIndex} className="text-left">THUẾ GTGT (VAT {parsedFreeVatRate}%)</th>
                  <th className="text-right">{formatCurrency(freeVatAmount)}</th>
                  {freePrintTrailingColumns > 0 && <th colSpan={freePrintTrailingColumns}></th>}
                </tr>
                <tr>
                  <th colSpan={freePrintAmountIndex} className="text-left">TỔNG THANH TOÁN</th>
                  <th className="text-right">{formatCurrency(freeGrandTotal)}</th>
                  {freePrintTrailingColumns > 0 && <th colSpan={freePrintTrailingColumns}></th>}
                </tr>
              </tfoot>
            </table>

            {/* Bảng tổng tiền chuẩn PO */}
            <div className="no-print border border-slate-300 rounded-lg overflow-hidden bg-slate-50 text-xs">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 font-bold text-slate-900">
                <span className="uppercase tracking-wider">TỔNG CỘNG TIỀN HÀNG:</span>
                <div className="flex items-center gap-6 font-mono-num">
                  <span className="font-black text-sm">{formatCurrency(freeSubTotal)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-1.5 font-bold text-slate-700 bg-white">
                <label className="uppercase tracking-wider text-slate-600">THUẾ GTGT (VAT <input aria-label="Tỷ lệ VAT" inputMode="decimal" value={freeVatRate} onChange={(event) => setFreeVatRate(event.target.value.replace(',', '.'))} className="inline-block w-10 rounded border border-slate-300 bg-white px-1 text-center font-mono-num font-bold text-slate-800" />%):</label>
                <span className="font-mono-num text-slate-800">{formatCurrency(freeVatAmount)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-2 font-black text-slate-950 bg-slate-100">
                <span className="uppercase tracking-wider">TỔNG THANH TOÁN (ĐÃ BAO GỒM VAT):</span>
                <span className="font-mono-num text-base">{formatCurrency(freeGrandTotal)}</span>
              </div>
            </div>
          </div>
        ) : (
        <div className="mt-5 w-full" onPaste={handleTablePaste}>
          <table className="po-print-grid po-excel-grid">
            <thead>
              <tr>
                {visibleTableColumns.map((col) => (
                  <th
                    key={col.key}
                    style={{
                      width: `${(((col.width || 100) / totalColumnWeights) * 100).toFixed(2)}%`,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
                {isEditMode && (
                  <th
                    style={{
                      width: `${((38 / totalColumnWeights) * 100).toFixed(2)}%`,
                    }}
                    className="no-print"
                  >
                    ⋮
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const isSelected = selectedRowIdx === index
                const qtyVal =
                  parseFloat(String(item.qty || 0).replace(/,/g, '')) || 0

                return (
                  <tr
                    key={item.id}
                    className={isSelected ? 'row-selected' : ''}
                    onClick={() => setSelectedRowIdx(index)}
                  >
                    {visibleTableColumns.map((col) => {
                      if (col.key === 'stt' || col.key === 'id') {
                        return (
                          <td
                            key={col.key}
                            className="text-center font-mono font-bold text-slate-600 bg-slate-50 select-none"
                          >
                            {index < 9 ? `0${index + 1}` : index + 1}
                          </td>
                        )
                      }

                      if (col.key === 'calculation') return <td key={col.key} className="px-2 py-1 no-print"><div className="text-xs text-slate-600">{item.priceSource === 'manual' ? 'Nhập tay' : item.quoteTitle || item.priceSource || 'Chưa có giá'}</div><button type="button" className="text-blue-700 underline text-xs" onClick={() => void showCalculation(item)}>Xem cách tính</button></td>
                      if (col.key === 'stockUomDisplay') return <td key={col.key} className="px-2 text-center">{systemMaterials.find(m => m.id === item.material_id)?.uom_default || '—'}</td>
                      if (col.key === 'priceUomDisplay') return <td key={col.key} className="px-2 text-center">{item.priceUom || supplierRules.find(r => r.id === item.priceRuleId)?.uom || item.uom || '—'}</td>
                      if (col.key === 'priceBasisQty') return <td key={col.key} className="px-2">{isEditMode && item.priceSource === 'manual' ? <input className="cell-input text-right" type="number" min="0.000001" step="any" aria-label={`Giá cho N đơn vị dòng ${index + 1}`} value={item.priceBasisQty ?? 1} onChange={e => updateItem(index, { priceBasisQty: Number(e.target.value) })} /> : <span>{item.priceBasisQty ?? 1}</span>}</td>
                      if (col.key === 'price') {
                        const priceDisplay = item.price != null ? formatSheetAmount(item.price) : ''
                        return (
                          <td key={col.key} className="relative p-1">
                            {isEditMode ? (
                              <div className="flex w-full items-center overflow-hidden">
                                <div className="flex items-center w-full">
                                  {item.priceSource === 'SUPPLIER_QUOTE' && (
                                    <span
                                      className="mr-1 bg-emerald-100 text-emerald-800 font-extrabold text-[9px] px-1 py-0.5 rounded border border-emerald-300 shrink-0 select-none"
                                      title={item.priceReason || 'Khớp theo báo giá NCC'}
                                    >
                                      NCC ✓
                                    </span>
                                  )}
                                  {item.isNegotiated && (
                                    <span
                                      className="mr-1 bg-amber-100 text-amber-800 font-extrabold text-[9px] px-1 py-0.5 rounded border border-amber-300 shrink-0 select-none"
                                      title="Giá thương lượng"
                                    >
                                      TL
                                    </span>
                                  )}
                                  {item.priceSource === 'manual' && !item.isNegotiated && (
                                    <span
                                      className="mr-1 bg-slate-100 text-slate-700 font-extrabold text-[9px] px-1 py-0.5 rounded border border-slate-300 shrink-0 select-none"
                                      title={item.manualReason || 'Giá nhập tay'}
                                    >
                                      Tay
                                    </span>
                                  )}
                                  <SheetAmountInput
                                    className={`cell-input text-right font-mono-num font-bold px-1 flex-1 ${
                                      item.isNegotiated
                                        ? 'text-amber-800 bg-amber-50'
                                        : item.priceSource === 'SUPPLIER_QUOTE'
                                          ? 'text-emerald-800'
                                          : 'text-slate-800'
                                    }`}
                                    value={item.sheetInfo?.has_sheet && item.sheetPreview && !item.isNegotiated ? item.sheetPreview.purchase_price : item.price}
                                    readOnly={Boolean(item.sheetInfo?.has_sheet) && !item.isNegotiated}
                                    title={item.sheetInfo?.has_sheet ? (item.isNegotiated ? 'Giá thương lượng: nhập đơn giá thỏa thuận' : 'Đơn giá do bảng tính báo giá tính từ số nhập lúc mua. Muốn sửa: chuột phải menu ⋮ chọn Giá thương lượng (TL).') : undefined}
                                    onTextChange={(text) => updatePrice(index, text)}
                                  />
                                </div>
                                {item.material_id && selectedSupplier?.id && (
                                <div className="shrink-0">
                                    <button
                                      type="button"
                                      className="px-1 text-[11px] leading-none text-amber-900"
                                      aria-label="Chọn dòng báo giá"
                                      title="Chọn dòng báo giá"
                                      onClick={() => setRulePickerIndex(index)}
                                    >
                                      ⋯
                                    </button>
                                    {rulePickerIndex === index && (
                                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation" onClick={() => setRulePickerIndex(null)}>
                                        <div className="w-full max-w-5xl rounded-lg bg-white p-4 shadow-xl" role="dialog" aria-modal="true" aria-label="Chọn dòng báo giá" onClick={(e) => e.stopPropagation()}>
                                          <PoRulePickerModal
                                            supplierId={selectedSupplier?.id ?? ""}
                                            itemId={item.material_id}
                                            selectedRuleId={item.priceRuleId ?? null}
                                            onSelect={(r) => {
                                              setRulePickerIndex(null)
                                              handleSelectCandidateRule(index, r.id)
                                            }}
                                            onClose={() => setRulePickerIndex(null)}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {item.sheetInfo?.has_sheet && isEditMode && (
                                  <div className="sr-only">
                                    {sheetBlockReason(item) && <span className="text-amber-700">{sheetBlockReason(item)}</span>}
                                    {item.sheetPreview && <span className="text-emerald-900">Giá tính: {formatSheetAmount(item.sheetPreview.purchase_price)} đ</span>}
                                    {!sheetBlockReason(item) && !item.sheetPreview && !item.sheetError && <span className="text-slate-500">Đang tính giá tự động...</span>}
                                    {item.sheetError && <span className="text-red-700">{item.sheetError}</span>}
                                  </div>
                                )}
                                {item.priceWarning && (
                                  <div
                                    className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 mt-0.5 leading-tight"
                                    title={item.priceReason || item.priceWarning}
                                  >
                                    ⚠️ {item.priceWarning}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="cell-view text-right font-mono-num font-bold text-slate-800 flex items-center justify-end gap-1">
                                {item.priceSource === 'SUPPLIER_QUOTE' && (
                                  <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">
                                    NCC ✓
                                  </span>
                                )}
                                {item.isNegotiated && (
                                  <span className="text-[9px] font-extrabold text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">
                                    TL
                                  </span>
                                )}
                                <span>{priceDisplay || '—'}</span>
                              </div>
                            )}
                          </td>
                        )
                      }

                      if (
                        col.key === 'total' ||
                        col.key === 'thanhtien' ||
                        col.key === 'lineTotal'
                      ) {
                        return (
                          <td key={col.key} className="cell-calc font-mono-num">
                            <div className="flex flex-col items-end justify-center py-0.5">
                              <span>{formatSheetAmount(item.lineTotal) || '0'}</span>
                              {item.priceSource === 'manual' && item.lineTotal > 0 && (
                                <span
                                  data-testid={`provisional-badge-${item.id}`}
                                  className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 mt-0.5 leading-tight font-medium"
                                >
                                  Tạm tính
                                </span>
                              )}
                            </div>
                          </td>
                        )
                      }

                      if (col.key === 'vol') {
                        return (
                          <td
                            key={col.key}
                            className="cell-calc font-mono-num text-center"
                          >
                            {(
                              (Number(item.t || 0) *
                                Number(item.w || 0) *
                                Number(item.l || 0) *
                                qtyVal) /
                              1000000000
                            ).toFixed(4)}
                          </td>
                        )
                      }

                      if (col.key === 'area') {
                        return (
                          <td
                            key={col.key}
                            className="cell-calc font-mono-num text-center"
                          >
                            {(
                              (Number(item.w || 0) *
                                Number(item.l || 0) *
                                qtyVal) /
                              1000000
                            ).toFixed(4)}
                          </td>
                        )
                      }

                      if (
                        col.key === 'qty' ||
                        col.key === 'quantity' ||
                        col.key === 'sl'
                      ) {
                        return (
                          <td key={col.key}>
                            {isEditMode ? (
                              <input
                                type="number"
                                min="0"
                                className="cell-input text-center font-mono-num font-bold text-slate-800"
                                value={item.qty === 0 ? '' : formatPoQuantity(item.qty)}
                                step="0.001"
                                onChange={(e) =>
                                  updateQty(index, e.target.value)
                                }
                                placeholder="0"
                              />
                            ) : (
                              <div className="cell-view text-center font-mono-num font-bold text-slate-800">
                                {formatPoQuantity(qtyVal)}
                              </div>
                            )}
                          </td>
                        )
                      }

                      // Quote-backed dependent fields remain free-text, but expose the
                      // same name-scoped suggestions as the packaging BOM.
                      const isQuoteDependentField = col.role === 'lookup' ||
                        ['uom', 't', 'w', 'l', 'density', 'dinh_luong', 'kind'].includes(col.key)
                      const dependentSuggestions = isQuoteDependentField && item.name
                        ? quoteRuleFieldSuggestions(item.name, col.key, supplierRules, col.canonical_key)
                        : []
                      if (isQuoteDependentField && item.name && quoteRuleFieldIsUsed(item.name, col.key, supplierRules, col.canonical_key)) {
                        return (
                          <td key={col.key} className={`text-${col.align || 'center'}`}>
                            {isEditMode ? (
                              <Typeahead<string>
                                items={dependentSuggestions}
                                value={null}
                                inputValue={String(item[col.key] ?? '')}
                                onInputChange={(value) => updateItem(index, { [col.key]: value })}
                                onChange={(value) => { if (value !== null) updateItem(index, { [col.key]: value }) }}
                                displayValue={(value) => value}
                                allowCustomValue={true}
                                showDropdownButton={false}
                                placeholder={col.label}
                                className="w-full"
                                inputClassName="cell-input text-center text-slate-800"
                                dropdownClassName="min-w-[220px] max-w-[360px] shadow-lg border border-slate-300"
                              />
                            ) : (
                              <div className="cell-view text-center text-slate-600">{item[col.key] || '—'}</div>
                            )}
                          </td>
                        )
                      }

                      if (col.key === 'uom') {
                        return (
                          <td key={col.key} className="text-center">
                            {isEditMode ? (
                              <input
                                type="text"
                                className="cell-input text-center text-slate-600"
                                value={item.uom || ''}
                                onChange={(e) =>
                                  updateItem(index, { uom: e.target.value })
                                }
                              />
                            ) : (
                              <div className="cell-view text-center text-slate-600">
                                {item.uom || '—'}
                              </div>
                            )}
                          </td>
                        )
                      }

                      if (col.key === 'grade') {
                        const gradeOpts = col.options || [
                          'AB',
                          'AC',
                          'BC',
                          'CC',
                        ]
                        return (
                          <td key={col.key} className="text-center">
                            {isEditMode ? (
                              <select
                                className="cell-input text-center font-bold text-blue-700 bg-transparent cursor-pointer"
                                value={item.grade || ''}
                                onChange={(e) =>
                                  updateItem(index, { grade: e.target.value })
                                }
                              >
                                <option value="">-- Phẩm cấp --</option>
                                {gradeOpts.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div className="cell-view text-center font-bold text-blue-700">
                                {item.grade || '—'}
                              </div>
                            )}
                          </td>
                        )
                      }

                      if (col.type === 'select' && col.options) {
                        return (
                          <td
                            key={col.key}
                            className={`text-${col.align || 'center'}`}
                          >
                            {isEditMode ? (
                              <select
                                className="cell-input text-center font-medium text-slate-800 bg-transparent cursor-pointer"
                                value={item[col.key] || ''}
                                onChange={(e) =>
                                  updateItem(index, {
                                    [col.key]: e.target.value,
                                  })
                                }
                              >
                                <option value="">-- {col.label} --</option>
                                {col.options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div className="cell-view text-center font-medium text-slate-800">
                                {item[col.key] || '—'}
                              </div>
                            )}
                          </td>
                        )
                      }

                      if (
                        col.type === 'number' ||
                        col.type === 'currency' ||
                        col.key === 't' ||
                        col.key === 'w' ||
                        col.key === 'l'
                      ) {
                        const isDim =
                          col.key === 't' || col.key === 'w' || col.key === 'l'
                        return (
                          <td
                            key={col.key}
                            className={`text-${col.align || 'center'}`}
                          >
                            {isEditMode ? (
                              <input
                                type="number"
                                className={`cell-input text-${col.align || 'center'} font-mono-num ${
                                  isDim
                                    ? 'font-bold text-blue-700'
                                    : 'text-slate-800'
                                }`}
                                value={item[col.key] ?? ''}
                                onChange={(e) =>
                                  updateItem(index, {
                                    [col.key]:
                                      e.target.value === ''
                                        ? ''
                                        : parseFloat(e.target.value) || 0,
                                  })
                                }
                                placeholder={col.label}
                              />
                            ) : (
                              <div
                                className={`cell-view text-${col.align || 'center'} font-mono-num ${
                                  isDim
                                    ? 'font-bold text-blue-700'
                                    : 'text-slate-800'
                                }`}
                              >
                                {item[col.key] ?? '—'}
                              </div>
                            )}
                          </td>
                        )
                      }

                      // Name column with autocomplete from active supplier quotes & system materials
                      if (col.key === 'name') {
                        const targetKey = 'name'
                        const hasNoQuoteForSupplier = Boolean(selectedSupplier && quoteRuleSuggestions.length === 0)
                        const inputPlaceholder = !selectedSupplier
                          ? 'Gõ tên hoặc chọn danh mục...'
                          : hasNoQuoteForSupplier
                            ? 'Chưa có báo giá NCC (chọn từ DM vật tư)...'
                            : col.label

                        return (
                          <td
                            key={col.key}
                            className={`text-${col.align || 'left'} p-0.5`}
                          >
                            {isEditMode ? (
                              <div className="flex items-center gap-0.5 w-full">
                                <div className="relative flex-1 min-w-0">
                                  <Typeahead<PoNameSuggestion>
                                    items={combinedNameSuggestions}
                                    value={null}
                                    inputValue={item[targetKey] || ''}
                                    onInputChange={(val) => {
                                      const patch: Partial<PoItemRow> = {
                                        [targetKey]: val,
                                        ...(targetKey === 'name' ? resetSheetOnNameChange(item, val) : {}),
                                      }
                                      updateItem(index, patch)
                                    }}
                                    allowCustomValue={true}
                                    showDropdownButton={false}
                                    placeholder={inputPlaceholder}
                                    displayValue={(s) => s.name || s.primaryText}
                                    filterFn={filterNameSuggestion}
                                    onChange={(s) => {
                                      if (!s) return
                                      if (s.type === 'quote' && s.rule && s.quote) {
                                        const patch = { ...quoteNameSelectionPatch({ name: s.name, rule: s.rule }, item), ...resetSheetOnNameChange(item, s.name || '') }
                                        if (!patch.material_id) {
                                          const match = systemMaterials.find(
                                            (m) => m.name.trim().toLowerCase() === (s.name || '').trim().toLowerCase(),
                                          )
                                          if (match) patch.material_id = match.id
                                        }
                                        updateItem(index, patch)
                                      } else if (s.type === 'material' && s.material) {
                                        const patch: Partial<PoItemRow> = {
                                          material_id: s.material.id,
                                          name: s.material.name,
                                          uom: s.material.uom_default || item.uom,
                                          ...resetSheetOnNameChange(item, s.material.name),
                                        }
                                        updateItem(index, patch)
                                      }
                                    }}
                                    renderItem={(s) => (
                                      <div className="py-0.5">
                                        <div className="font-semibold text-slate-800 flex items-center justify-between gap-2">
                                          <span className="flex items-center gap-1.5 min-w-0">
                                            {s.type === 'quote' ? (
                                              <span className="px-1 py-0.2 bg-emerald-100 text-emerald-800 font-bold text-[9px] rounded border border-emerald-200 shrink-0">
                                                Báo giá
                                              </span>
                                            ) : (
                                              <span className="px-1 py-0.2 bg-blue-100 text-blue-800 font-bold text-[9px] rounded border border-blue-200 shrink-0">
                                                Vật tư
                                              </span>
                                            )}
                                            <span className="truncate">{s.primaryText}</span>
                                          </span>
                                          {s.material?.uom_default ? (
                                            <span className="text-slate-500 text-[11px] whitespace-nowrap">
                                              {s.material.uom_default}
                                            </span>
                                          ) : null}
                                        </div>
                                        {s.secondaryText && (
                                          <div className="text-[11px] text-slate-500 font-normal truncate mt-0.5">
                                            {s.secondaryText}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    className="w-full border-0 rounded-none focus-within:ring-1 focus-within:ring-emerald-600"
                                    inputClassName="cell-input text-left font-bold text-slate-800 px-1 py-0.5 w-full"
                                    dropdownClassName="min-w-[320px] max-w-[500px] shadow-lg border border-slate-300"
                                  />
                                </div>
                                {item.material_id ? (
                                  <span
                                    className="px-1 text-[9px] font-mono font-bold bg-blue-100 text-blue-800 border border-blue-300 rounded shrink-0 select-none"
                                    title={`Đã liên kết vật tư: ${systemMaterials.find((m) => m.id === item.material_id)?.code || item.material_id}`}
                                  >
                                    ✓ VT
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <div className="cell-view text-left font-bold text-slate-800">
                                {item[targetKey] || '—'}
                              </div>
                            )}
                          </td>
                        )
                      }

                      if (col.key.startsWith('sheet__')) {
                        const sheetName = col.key.slice('sheet__'.length)
                        // Sheet cells use the active-rule chain below so every value is
                        // filtered by the choices already entered in the same PO row.
                        const rulesForRow = item.material_id ? filterRulesByItemName(sheetRulesByKey[(selectedSupplier?.id ?? '') + '|' + item.material_id] ?? [], item.name) : []
                        const choices = item.sheetChoices ?? {}
                        const optionList = sheetOptionsFor(rulesForRow, choices, sheetName, poSheetColumns.map((c) => c.name))
                        const isInput = item.sheetInfo
                          ? item.sheetInfo.input_names.includes(sheetName)
                          : rulesForRow.some((r) => (r.input_names ?? []).includes(sheetName))
                        const mapped = item.sheetInfo?.mappings?.[sheetName]
                        if (isInput && isEditMode) {
                          return (
                            <td key={col.key} className="px-1 py-1 text-right">
                              <input
                                type={sheetInputTypeForColumn(sheetName)}
                                className={'cell-input w-full text-right ' + ((item.sheetInputs?.[sheetName] ?? '') === '' ? 'border border-amber-400' : '')}
                                aria-label={'Nhập lúc mua ' + sheetName + ' dòng ' + (index + 1)}
                                value={item.sheetInputs?.[sheetName] ?? ''}
                                onChange={(e) => updateSheetInput(index, sheetName, e.target.value)}
                              />
                            </td>
                          )
                        }
                        if (isEditMode && rulesForRow.length > 0 && !isInput) {
                          const current = choices[sheetName] ?? displayCell(mapped)
                          const opts = current && !optionList.includes(current) ? [...optionList, current] : optionList
                          return (
                            <td key={col.key} className="px-1 py-1 text-right">
                              <QuoteChoiceInput
                                ariaLabel={'Quy cách ' + sheetName + ' dòng ' + (index + 1)}
                                value={current}
                                options={opts}
                                onCommit={(v) => setSheetChoice(index, sheetName, v, rulesForRow)}
                              />
                            </td>
                          )
                        }
                        return (
                          <td key={col.key} className="px-2 py-1 text-right font-mono-num text-xs">
                            {displayCell(choices[sheetName] ?? (isInput ? item.sheetInputs?.[sheetName] : mapped))}
                          </td>
                        )
                      }

                      // Spec column with Typeahead autocomplete from active supplier quotes
                      if (col.key === 'spec') {
                        const scopedSuggestions = item.name
                          ? quoteRuleSuggestions.filter((suggestion) => quoteRuleMatchesItemName(suggestion.rule, item.name))
                          : []
                        return (
                          <td
                            key={col.key}
                            className={`text-${col.align || 'left'}`}
                          >
                            {isEditMode ? (
                              <div className="relative">
                                <Typeahead<QuoteRuleSuggestion>
                                  items={scopedSuggestions}
                                  value={null}
                                  inputValue={item.spec || ''}
                                  onInputChange={(val) =>
                                    updateItem(index, { spec: val })
                                  }
                                  allowCustomValue={true}
                                  showDropdownButton={false}
                                  placeholder={col.label}
                                  displayValue={(s) => s.spec || s.primaryText}
                                  filterFn={filterQuoteRuleSuggestion}
                                  onChange={(s) => {
                                    if (s) {
                                      const patch = sheetSafePatch(quoteRuleToPoItemPatch(s.rule, s.quote, quoteType, item))
                                      updateItem(index, patch)
                                    }
                                  }}
                                  renderItem={(s) => (
                                    <div className="py-0.5">
                                      <div className="font-semibold text-slate-800 flex items-center justify-between gap-2">
                                        <span>{s.spec || s.primaryText}</span>
                                        {s.rule.unit_price ? (
                                          <span className="font-mono text-emerald-700 font-bold whitespace-nowrap">
                                            {Number(s.rule.unit_price).toLocaleString('vi-VN')} đ
                                          </span>
                                        ) : null}
                                      </div>
                                      <div className="text-[11px] text-slate-500 font-normal truncate mt-0.5">
                                        {s.name ? `${s.name} · ` : ''}{s.secondaryText}
                                      </div>
                                    </div>
                                  )}
                                  className="w-full border-0 rounded-none focus-within:ring-1 focus-within:ring-emerald-600"
                                  inputClassName="cell-input text-left font-bold text-slate-800 px-1 py-0.5"
                                  dropdownClassName="min-w-[280px] max-w-[460px] shadow-lg border border-slate-300"
                                />
                              </div>
                            ) : (
                              <div className="cell-view text-left font-bold text-slate-800">
                                {item.spec || '—'}
                              </div>
                            )}
                          </td>
                        )
                      }

                      // Default text column (spec, notes, etc.)
                      const isBold = col.key === 'spec'
                      return (
                        <td
                          key={col.key}
                          className={`text-${col.align || 'left'}`}
                        >
                          {isEditMode ? (
                            <input
                              type="text"
                              className={`cell-input text-${col.align || 'left'} ${
                                isBold
                                  ? 'font-bold text-slate-800'
                                  : 'text-slate-700'
                              }`}
                              value={item[col.key] || ''}
                              onChange={(e) =>
                                updateItem(index, { [col.key]: e.target.value })
                              }
                              placeholder={col.label}
                            />
                          ) : (
                            <div
                              className={`cell-view text-${col.align || 'left'} ${
                                isBold
                                  ? 'font-bold text-slate-800'
                                  : 'text-slate-700'
                              }`}
                            >
                              {item[col.key] || '—'}
                            </div>
                          )}
                        </td>
                      )
                    })}

                    {/* HÀNH ĐỘNG 3 CHẤM (CHỈ KHI CHỈNH SỬA) */}
                    {isEditMode && (
                      <td className="text-center no-print py-1 px-1 relative">
                        <div className="relative row-menu-container flex items-center justify-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              // Menu cố định theo màn hình để không bị bảng cắt mất (mất nút Xóa dòng ở các dòng cuối).
                              const rect = e.currentTarget.getBoundingClientRect()
                              const openUp = window.innerHeight - rect.bottom < 230
                              setRowMenuPos(openUp
                                ? { bottom: window.innerHeight - rect.top + 4, right: Math.max(8, window.innerWidth - rect.right) }
                                : { top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) })
                              setActiveMenuRowIdx(
                                activeMenuRowIdx === index ? null : index,
                              )
                            }}
                            className="h-7 w-7 inline-flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold border border-slate-300 shadow-2xs transition cursor-pointer select-none"
                            title="Tác vụ dòng (⋮)"
                          >
                            <MoreVertical className="w-4 h-4 text-slate-700" />
                          </button>
                          {activeMenuRowIdx === index && (
                            <div
                              style={{ position: 'fixed', right: rowMenuPos?.right ?? 8, ...(rowMenuPos?.bottom !== undefined ? { bottom: rowMenuPos.bottom } : { top: rowMenuPos?.top ?? 0 }) }}
                              className="z-[200] bg-white border border-slate-300 rounded-lg shadow-2xl min-w-[195px] py-1 text-xs text-left divide-y divide-slate-100"
                            >
                              <div className="py-0.5">
                                <button
                                  type="button"
                                  disabled={index === 0}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    moveRowUp(index)
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-100 text-slate-700 font-medium transition disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <ArrowUp className="w-4 h-4 text-blue-600 shrink-0" />
                                  <span>Di chuyển lên</span>
                                </button>
                                <button
                                  type="button"
                                  disabled={index === items.length - 1}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    moveRowDown(index)
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-100 text-slate-700 font-medium transition disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <ArrowDown className="w-4 h-4 text-blue-600 shrink-0" />
                                  <span>Di chuyển xuống</span>
                                </button>
                              </div>
                              <div className="py-0.5">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleNegotiatedPrice(index)
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-amber-50 text-amber-800 font-medium transition"
                                >
                                  <Tag className="w-4 h-4 text-amber-600 shrink-0" />
                                  <span>
                                    {item.isNegotiated
                                      ? 'Khóa giá chuẩn'
                                      : 'Giá thương lượng (TL)'}
                                  </span>
                                </button>
                              </div>
                              <div className="py-0.5">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    deleteRow(index)
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-red-50 text-red-600 font-medium transition"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500 shrink-0" />
                                  <span>Xóa dòng</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>

            {/* TFOOT - ALL BORDERS ALIGNED */}
            <tfoot>
              {/* Row 1: Tổng cộng số lượng & tiền hàng */}
              <tr className="bg-slate-100 font-bold text-slate-900">
                <td
                  colSpan={firstSummaryColIdx}
                  className="text-right px-4 py-2 uppercase tracking-wider text-xs"
                >
                  TỔNG CỘNG SỐ LƯỢNG / TIỀN HÀNG:
                </td>
                {visibleTableColumns.slice(firstSummaryColIdx).map((col) => {
                  if (col.key === 'area') {
                    return (
                      <td
                        key={col.key}
                        className="text-center px-1 py-1 font-mono-num font-bold text-slate-900 text-xs"
                      >
                        {totalArea.toFixed(2)} m²
                      </td>
                    )
                  }
                  if (col.key === 'vol') {
                    return (
                      <td
                        key={col.key}
                        className="text-center px-1 py-1 font-mono-num font-bold text-slate-900 text-xs"
                      >
                        {totalVol.toFixed(4)} m³
                      </td>
                    )
                  }
                  if (
                    col.key === 'qty' ||
                    col.key === 'quantity' ||
                    col.key === 'sl'
                  ) {
                    return (
                      <td
                        key={col.key}
                        className="text-center px-2 py-2 font-mono-num font-bold text-slate-900 text-sm"
                      >
                        {formatPoQuantity(totalQty)}
                      </td>
                    )
                  }
                  if (col.key === 'price') {
                    return (
                      <td key={col.key} className="text-center text-slate-400">
                        —
                      </td>
                    )
                  }
                  if (
                    col.key === 'total' ||
                    col.key === 'thanhtien' ||
                    col.key === 'lineTotal'
                  ) {
                    const hasManual = items.some(
                      (i) => i.priceSource === 'manual' && i.lineTotal > 0,
                    )
                    return (
                      <td
                        key={col.key}
                        className="text-right px-3 py-2 font-mono-num font-black text-slate-900 text-sm"
                      >
                        <div className="flex flex-col items-end">
                          <span>{formatCurrency(subTotal)}</span>
                          {hasManual && (
                            <span className="text-[10px] text-amber-700 font-medium mt-0.5">
                              (Gồm dòng tạm tính)
                            </span>
                          )}
                        </div>
                      </td>
                    )
                  }
                  return <td key={col.key}></td>
                })}
                {isEditMode && <td></td>}
              </tr>

              {/* Row 2: VAT theo PO */}
              <tr className="bg-slate-50 font-bold text-slate-700">
                <td
                  colSpan={totalColIdx}
                  className="text-right px-4 py-1.5 uppercase tracking-wider text-xs text-slate-600"
                >
                  THUẾ GIÁ TRỊ GIA TĂNG (VAT {parseNumberVi(standardVatRate) ?? 0}%):
                </td>
                <td className="text-right px-3 py-1.5 font-mono-num font-bold text-slate-800 text-xs">
                  <input aria-label="VAT %" type="text" inputMode="decimal" value={standardVatRate} onChange={(e) => setStandardVatRate(e.target.value)} className="mr-2 w-14 border-b border-slate-400 bg-transparent text-center outline-none" />
                  {formatCurrency(vatAmount)}
                </td>
                <td
                  colSpan={Math.max(
                    1,
                    visibleTableColumns.length - totalColIdx - 1 + (isEditMode ? 1 : 0),
                  )}
                ></td>
              </tr>

              {/* Row 3: Tổng thanh toán */}
              <tr className="bg-slate-100 font-black text-slate-900">
                <td
                  colSpan={totalColIdx}
                  className="text-right px-4 py-2 uppercase tracking-wider text-slate-950 text-xs font-black"
                >
                  TỔNG THANH TOÁN (ĐÃ BAO GỒM VAT):
                </td>
                <td className="text-right px-3 py-2 font-mono-num font-black text-slate-950 text-base">
                  {formatCurrency(grandTotal)}
                </td>
                <td
                  colSpan={Math.max(
                    1,
                    visibleTableColumns.length - totalColIdx - 1 + (isEditMode ? 1 : 0),
                  )}
                ></td>
              </tr>
            </tfoot>
          </table>
        </div>
        )}

        {/* 4. Lưu ý & Liên hệ giao nhận */}
        <div className="mt-5 grid grid-cols-2 gap-5 text-xs text-slate-700">
          <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-lg po-print-notes">
            <h4 className="font-bold text-amber-900 mb-1.5 uppercase text-[11px] tracking-wide">
              Lưu ý quan trọng
            </h4>
            {isEditMode ? (
              <textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                className="w-full bg-transparent border-none outline-none resize-none h-16 text-amber-950 font-medium text-xs leading-relaxed"
                placeholder="Nhập ghi chú giao nhận vật tư..."
              />
            ) : (
              <div className="h-16 text-amber-950 whitespace-pre-wrap leading-relaxed text-[11px]">
                {orderNotes || 'Không có ghi chú.'}
              </div>
            )}
          </div>

          <div className="flex flex-col justify-end items-end text-right space-y-1">
            <p className="font-bold text-slate-900 text-xs">Liên hệ giao nhận NVL:</p>
            <p className="text-slate-800 font-medium">Ms. Thủy — Phòng Mua Hàng</p>
            <p className="font-mono-num font-bold text-blue-700 text-sm">
              012.856.231 &nbsp;|&nbsp; 0961.707.990
            </p>
          </div>
        </div>

        {/* 5. Chữ Ký 5 Bên (Có Thể Chỉnh Sửa) */}
        <div className="mt-8 pt-4 border-t border-slate-300">
          <PoSignerEditor
            value={signatures.map((s): PoSigner => ({ title: s.role, name: s.name }))}
            editable={isEditMode || !isPoEditMode}
            allowDefaultReset={!isPoEditMode}
            onChange={(next) => setSignatures(next.map((s) => ({ role: s.title, name: s.name })))}
          />
        </div>

        {/* 6. Footer */}
        <div className="mt-8 pt-3 border-t border-slate-300 flex justify-between items-center text-[10px] text-slate-400 font-mono-num">
          <span>Hệ thống ERP Mộc Phát • Module Thu mua & Đơn Hàng PO</span>
          <span>Thời gian: {formatDateFull(new Date())}</span>
        </div>
      </div>
      </div>

      <PoPriceSnapshotModal open={calculationOpen} onClose={() => { calculationSequence.current++; setCalculationOpen(false) }} snapshot={calculationSnapshot} loading={calculationLoading} error={calculationError} provisional />
      {/* Paste Modal */}
      {pasteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs no-print">
          <div className="w-full max-w-xl rounded-xl border border-slate-700 bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-5 py-3 text-white">
              <h3 className="text-xs font-black uppercase tracking-wider">
                Paste Hàng Loạt Dữ Liệu Từ Excel
              </h3>
              <button
                type="button"
                onClick={() => setPasteModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-slate-600 bg-slate-50 p-2.5 rounded border border-slate-200">
                Copy các ô trong Excel (Ctrl+C), dán vào khung bên dưới và bấm nút{' '}
                <strong>"Nạp vào bảng đơn hàng"</strong> (hoặc nhấn Ctrl+V trực tiếp trên
                bảng).
              </div>
              <textarea
                rows={8}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                className="w-full rounded border border-slate-300 p-2.5 font-mono text-xs focus:border-blue-500 outline-none"
                placeholder="Dán dữ liệu từ Excel vào đây (Ctrl+V)..."
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <button
                type="button"
                onClick={() => setPasteModalOpen(false)}
                className="px-3.5 py-1.5 border border-slate-300 rounded text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                onClick={() => applyExcelPaste(pasteText)}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-xs font-bold text-white shadow-2xs"
              >
                Nạp vào bảng đơn hàng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Material Creation Modal */}

      {/* Toast Notification */}
      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-50 rounded-lg bg-slate-900 px-4 py-3 text-xs font-semibold text-white shadow-2xl border border-slate-700 flex items-center gap-2 no-print">
          <span>{toastMsg}</span>
        </div>
      )}
    </div>
  )
}
